import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';
import { FormatInt } from '../../../utils/format';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { FilterLayoutByArea } from '../areaFilter';
import { useWarehouseLabels } from '../warehouseLabels';
import ConfirmPasswordModal from './ConfirmPasswordModal';
import { AreaEditModal, BlockAddModal, BlockEditModal, RackEditModal } from './MapEntityModals';
import MapPalette, { PaletteItemLabelKey } from './MapPalette';
import RackDraftPanel from './RackDraftPanel';
import {
    AREA_TYPES,
    AddBlockToLayout,
    AreaHandleScreenPositions,
    AreaScreenRect,
    BLOCK_DELETE_BLOCKED_MSG,
    BlockKey,
    BuildCellMaps,
    BuildStatusOverlayIndex,
    CellScreenRect,
    ComputeAreaDropRect,
    ComputeAreaResizeBounds,
    ComputeRackGeometry,
    ComputeRackResizePreviewCells,
    ComputeViewTransform,
    CreateAreaAtCell,
    CreateFixedInLayout,
    CreateRackInLayout,
    DefaultLayout,
    DeleteAreaFromLayout,
    DeriveRackAxis,
    FIXED_DELETE_BLOCKED_MSG,
    FindAddableBlockAt,
    FindBlockByRackBay,
    FindFixedByCode,
    FindRackByPrefix,
    FormatBayCode,
    GRID_LIMITS,
    GetAllRackCells,
    GetAreaById,
    GetBlockById,
    GetFixedById,
    GetObjectAt,
    GetRackBlocks,
    GetRackById,
    GetSelectionHandleAt,
    GetTopAreaAt,
    InsideGrid,
    InsideView,
    MetaForArea,
    MoveAreaInLayout,
    MoveFixedInLayout,
    MoveRackInLayout,
    NextFreeRackPrefix,
    NormalizeLayout,
    NormalizeRackPrefix,
    PointInsideRect,
    RACK_DRAFT_DEFAULTS,
    RACK_REAL_MAX_BAYS,
    RACK_REAL_MAX_LEVELS,
    RackBounds,
    RackHandleScreenPos,
    RemoveBlockFromLayout,
    RemoveRackFromLayout,
    ResizeAreaInLayout,
    ResizeRackInLayout,
    ScreenToCell,
    SetGridInLayout,
    SideCell,
    UpdateAreaTypeInLayout,
    UpdateBlockInLayout,
    UpdateRackInLayout,
    ValidateWorld,
} from './mapModel';

/**
 * WarehouseMap — base ÚNICA de render do mapa do galpão (canvas) com MODOS
 * plugáveis. Na 4ª rodada (10/08) o componente absorveu o editor INLINE (o
 * MapLayoutEditorModal morreu) e passou a criar DADOS REAIS junto com o
 * desenho. Desde 02/09/2026 NÃO EXISTE MAIS RUA: o que se desenha é o RACK
 * (porta-pallet de prefixo `A`, `B`…, um corredor + a fileira de blocos de UM
 * lado, blocos sequenciais 1..N). Contrato de props (a página programa contra
 * isto):
 *
 * <WarehouseMap
 *   mode="status"|"edit"          — a página controla o modo; o mapa cuida do resto
 *   layout                        — layout salvo (GET /warehouse/map-layout)
 *   onLayoutChange(layout)        — chamado quando um layout PERSISTIDO muda
 *                                   (Salvar do editor, criação de rack, fixa
 *                                   posicionada) — nunca com rascunho não salvo
 *   areaFilter={areaId|null}      — desenha SÓ a área (e o que está dentro) —
 *                                   recorte VISUAL; vale nos dois modos e a
 *                                   edição continua permitida (validação segue
 *                                   no mundo inteiro)
 *   selection                     — seleção PERSISTENTE vinda da página
 *                                   ({kind:'block'|'rack'|'fixed'|'cell', rack?, bay?, code?})
 *   onSelect(sel)                 — clique no modo status: bloco individual →
 *                                   {kind:'block', rack, bay}; célula de
 *                                   corredor → {kind:'rack', rack}; fixa →
 *                                   {kind:'fixed', code}; célula vazia →
 *                                   {kind:'cell'} (a página limpa a seleção)
 *   fitHeight                     — px: o recorte visível INTEIRO cabe em
 *                                   largura×fitHeight, sem rolagem
 *   onRackCreated(rack)           — depois do POST generate-rack com sucesso
 *                                   (ou de um rack existente ser posicionado)
 *   placeRequest                  — {type:'fixed', code} → modo posicionar com
 *                                   ghost; {type:'rack'} → arma o mesmo
 *                                   traçado de dois cliques do chip da paleta;
 *                                   null → nada
 *   onPlaceResolved(ok)           — posicionamento concluído (true) ou
 *                                   cancelado com ESC/Cancelar (false)
 *   overlay / highlight           — extras do modo status (linhas do
 *                                   GET /locations/map `{rack, bay, ...}` para
 *                                   colorir blocos; pisca-pisca lista→mapa via
 *                                   pulseKey em `{rack, bay, pulseKey}`)
 *   route                         — modo `route` (M5-3): o DESENHO da rota em
 *                                   coordenadas de CÉLULA
 *                                   ({polyline, approach_legs, final_leg,
 *                                   start, stops}) — sai de
 *                                   `BuildRouteMapOverlay` (routeGeometry.js),
 *                                   que divide os metros do motor por
 *                                   `map_cell_m`. O mapa NÃO calcula rota:
 *                                   desenha a que o motor devolveu
 *   onRouteStopClick(itemKey)     — clique numa PARADA (montagem da rota
 *                                   manual); sem ele as paradas só informam
 * />
 *
 * Modo `edit` (inline): paleta em faixa (chips de ÁREA arrastáveis + chip de
 * RACK que arma o traçado + chips dos RACKS SEM POSIÇÃO no mapa) + barra Salvar
 * (re-senha via ConfirmPasswordModal + PUT) / Descartar. O RASCUNHO é interno
 * ao mapa — a página só recebe onLayoutChange quando algo é persistido.
 *
 * Criar RACK é um fluxo ÚNICO: chip "Rack" → 1º clique no mapa (início) → 2º
 * clique (fim) → painel de parâmetros (RackDraftPanel, na lateral: prefixo,
 * quantos blocos, andares, lado dos blocos, pesos, largura) → Criar.
 * Orientação e comprimento NÃO são campos: saem do traçado (|dx| >= |dy| →
 * horizontal; senão vertical). Entre os cliques o corredor + blocos seguem o
 * cursor; clicar de novo refaz o traçado sem perder os parâmetros; ESC cancela
 * o gesto inteiro. O Criar chama POST generate-rack; só com o dado real criado
 * o rack entra no desenho e o layout é salvo (PUT). Falha do PUT após generate
 * OK → banner "mapa não salvo — salvar de novo" (o dado real já existe).
 *
 * POSICIONAR RACK EXISTENTE: um rack que já vive em `wms_locations` (chega pelo
 * `overlay`) e não está no desenho aparece na paleta; o mesmo traçado de dois
 * cliques o desenha — SEM gerar dado — e o layout é salvo. Os vãos vivos do
 * cadastro viram blocos; vão apagado vira gap.
 *
 * FIT (r5, item 5): os dois modos desenham com a MESMA moldura (MAP_FRAME) e
 * medem o mesmo container (ResizeObserver no wrap) — entrar em edição não
 * encolhe o mapa. Por isso a paleta é faixa horizontal, e não coluna.
 *
 * Hover POR ELEMENTO (correção explícita do dono): o hover destaca APENAS o
 * elemento da célula sob o cursor — um bloco individual, UMA célula de
 * corredor, uma fixa — nunca o rack inteiro. A seleção persistente pode
 * contornar o rack (clique no corredor → kind 'rack').
 *
 * Exclusões: rack e bloco têm exclusão COM SENHA (o backend soft-deleta os
 * endereços reais só se estiverem vazios; o desenho some depois); fixa não tem
 * exclusão; áreas seguem livres, desde que não carreguem racks junto.
 *
 * O canvas usa o TEMA do app (CSS vars `--mono-*`, re-lidas no evento
 * `themechange`). Modos futuros (M5/M9): route/heatmap/allocate entram
 * registrando um renderer novo em MODE_RENDERERS — a base não muda.
 */

/**
 * Moldura do desenho — UMA só (r5, item 5 do dono: "o fit do modo edição é o
 * mesmo do modo status"). O editor desenhava com MAP_MARGIN (46px de moldura
 * em cada lado, para as réguas) e por isso o mapa ENCOLHIA ao entrar no modo
 * edição: 92px a menos de espaço útil em cada eixo, sobre os ~450px do fit.
 * Agora os dois modos usam a mesma moldura fina e as réguas do editor são
 * desenhadas DENTRO dela (fonte compacta + clamp na borda) — a célula e a área
 * desenhada ficam idênticas nos dois modos.
 */
const MAP_FRAME = 14;

/** Duração do pisca-pisca do highlight (ms). */
const PULSE_MS = 2000;

const BLOCK_UPDATED_MSG = 'Bloco atualizado.';
// Bloco desenhado sem rack/bloco reais não tem contrapartida no cadastro:
// gravar largura "para o desenho" seria recriar a armadilha.
const BLOCK_WIDTH_NO_REAL_BLOCK_MSG =
    'Este bloco ainda não tem rack/bloco reais no cadastro — a largura do vão só pode ser gravada em bloco gerado pela criação do rack.';
const BLOCK_WIDTH_SAVE_FAILED_MSG = 'Não foi possível gravar a largura no cadastro do bloco.';

const HANDLE_CURSORS = {
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    nw: 'nwse-resize',
    se: 'nwse-resize',
};

// Cores de status do TEMA CLARO — exportadas porque a legenda da página as
// consome como fonte única (chips DOM). O canvas usa a variante do tema ativo
// (palette.status), que no claro é exatamente esta.
export const STATUS_BLOCK_STYLES = {
    ok: { fill: '#dcfce7', stroke: '#4ade80', text: '#166534' },
    // PARCIAL (25/08): algumas posições bloqueadas, não todas — amarelo, entre
    // o verde (livre) e o âmbar (bloqueado). Antes 1 de 5 bloqueadas pintava o
    // bloco inteiro de âmbar e mentia sobre a capacidade.
    partial: { fill: '#fef9c3', stroke: '#facc15', text: '#854d0e' },
    blocked: { fill: '#fde68a', stroke: '#d97706', text: '#92400e' },
    none: { fill: '#f1f5f9', stroke: '#cbd5e1', text: '#94a3b8' },
};

// M9 — TERMÔMETRO de ocupação (§8.1.3): verde vazio → vermelho cheio. Exportado
// pela mesma razão do bloco acima: a legenda da página tem que pintar o chip
// com a cor EXATA que o canvas pinta o bloco. Duas tabelas de cor produziriam
// um bloco laranja no mapa e amarelo na legenda.
//
// ⚠️ A FAIXA vem do SERVIDOR (`heat_band` em occupancyMetrics.js). O navegador
// escolhe a COR da faixa; ele nunca recalcula o corte — senão o mapa e o
// relatório poderiam discordar sobre o mesmo bloco.
export const HEAT_BLOCK_STYLES = {
    EMPTY: { fill: '#f0fdf4', stroke: '#86efac', text: '#166534' },
    LOW: { fill: '#dcfce7', stroke: '#4ade80', text: '#166534' },
    MEDIUM: { fill: '#fef9c3', stroke: '#facc15', text: '#854d0e' },
    HIGH: { fill: '#fed7aa', stroke: '#fb923c', text: '#9a3412' },
    FULL: { fill: '#fecaca', stroke: '#ef4444', text: '#991b1b' },
    // Bloco 100% bloqueado: não é "vazio à espera de carga", é fora de
    // operação. Verde ali convidaria a guardar pallet onde ninguém alcança.
    UNAVAILABLE: { fill: '#e5e7eb', stroke: '#9ca3af', text: '#4b5563' },
    NONE: { fill: '#f1f5f9', stroke: '#cbd5e1', text: '#94a3b8' },
};

const HEAT_BLOCK_STYLES_DARK = {
    EMPTY: { fill: 'rgba(74, 222, 128, 0.10)', stroke: '#4ade80', text: '#bbf7d0' },
    LOW: { fill: 'rgba(74, 222, 128, 0.24)', stroke: '#4ade80', text: '#bbf7d0' },
    MEDIUM: { fill: 'rgba(250, 204, 21, 0.24)', stroke: '#facc15', text: '#fef08a' },
    HIGH: { fill: 'rgba(251, 146, 60, 0.28)', stroke: '#fb923c', text: '#fed7aa' },
    FULL: { fill: 'rgba(248, 113, 113, 0.30)', stroke: '#f87171', text: '#fecaca' },
    UNAVAILABLE: { fill: 'rgba(163, 163, 163, 0.16)', stroke: '#737373', text: '#d4d4d4' },
    NONE: { fill: 'rgba(38, 38, 38, 0.7)', stroke: '#525252', text: '#a3a3a3' },
};

/*****************************
 * Tema do canvas (CSS vars do app)
 *****************************/

// Lê os tokens `--mono-*` (triplets RGB do index.css) e monta a paleta de
// desenho do tema ativo. Chamada UMA vez por render de tema: o estado inicial
// lê no mount e o evento `themechange` (disparado pelo ThemeContext depois de
// aplicar as vars) re-lê. Os acentos (azul/âmbar/verde/roxo/índigo) são fixos
// por tema — mesma convenção dos pares claro/escuro do tailwind no app.
function readMapPalette() {
    const root = document.documentElement;
    const styles = window.getComputedStyle(root);
    const isDark = root.classList.contains('dark');
    const mono = (level, alpha = 1, fallback = '115 115 115') => {
        const triplet = styles.getPropertyValue(`--mono-${level}`).trim() || fallback;
        return alpha >= 1 ? `rgb(${triplet})` : `rgb(${triplet} / ${alpha})`;
    };

    if (isDark) {
        return {
            isDark,
            surface: mono(900, 1, '23 23 23'),
            gridLine: mono(50, 0.07, '250 250 250'),
            ruler: mono(400, 1, '163 163 163'),
            corridorFill: mono(800, 0.9, '38 38 38'),
            corridorStroke: mono(600, 1, '82 82 82'),
            corridorText: mono(300, 1, '212 212 212'),
            hoverOutline: mono(400, 0.85, '163 163 163'),
            hoverCell: 'rgba(96, 165, 250, 0.7)',
            selection: '#4ade80',
            handleFill: mono(950, 1, '10 10 10'),
            handleStroke: '#60a5fa',
            moveStroke: 'rgba(167, 139, 250, 0.95)',
            moveFill: 'rgba(167, 139, 250, 0.14)',
            resizeStroke: 'rgba(96, 165, 250, 0.95)',
            resizeFill: 'rgba(96, 165, 250, 0.14)',
            ghostAisleFill: 'rgba(96, 165, 250, 0.22)',
            ghostBlockFill: 'rgba(192, 132, 252, 0.20)',
            invalidFill: 'rgba(248, 113, 113, 0.18)',
            invalidStroke: 'rgba(248, 113, 113, 0.9)',
            block: { fill: 'rgba(96, 165, 250, 0.22)', stroke: mono(500, 1, '115 115 115'), text: '#bfdbfe' },
            fixed: { fill: 'rgba(129, 140, 248, 0.22)', stroke: '#818cf8', text: '#c7d2fe' },
            status: {
                ok: { fill: 'rgba(74, 222, 128, 0.18)', stroke: '#4ade80', text: '#bbf7d0' },
                // PARCIAL (25/08): algumas bloqueadas, não todas.
                partial: { fill: 'rgba(250, 204, 21, 0.18)', stroke: '#facc15', text: '#fef08a' },
                blocked: { fill: 'rgba(251, 191, 36, 0.20)', stroke: '#fbbf24', text: '#fde68a' },
                none: { fill: mono(800, 0.7, '38 38 38'), stroke: mono(600, 1, '82 82 82'), text: mono(400, 1, '163 163 163') },
            },
            heat: HEAT_BLOCK_STYLES_DARK,
            // Hachura das posições bloqueadas (§8.1.3): riscos por cima do bloco.
            hatch: 'rgba(163, 163, 163, 0.75)',
            pulse: '#60a5fa',
            // Rota desenhada SOBRE o mapa (M5-3): a linha do percurso, a perna
            // de 90° de cada parada e a perna FINAL (âmbar, com seta) — o que
            // responde "ao chegar, para que lado eu viro".
            route: {
                path: '#60a5fa',
                approach: 'rgba(163, 163, 163, 0.9)',
                final: '#fbbf24',
                start: mono(300, 1, '212 212 212'),
                startFill: mono(950, 1, '10 10 10'),
                stopFill: mono(950, 1, '10 10 10'),
                stopStroke: mono(400, 1, '163 163 163'),
                stopText: mono(300, 1, '212 212 212'),
                stopPickedFill: '#60a5fa',
                stopPickedText: '#0a0a0a',
                dim: mono(800, 0.55, '38 38 38'),
                dimStroke: mono(700, 1, '64 64 64'),
                dimText: mono(500, 1, '115 115 115'),
            },
        };
    }

    return {
        isDark,
        surface: '#ffffff',
        gridLine: mono(900, 0.09, '23 23 23'),
        ruler: mono(500, 1, '115 115 115'),
        corridorFill: mono(200, 0.85, '229 229 229'),
        corridorStroke: mono(400, 1, '163 163 163'),
        corridorText: mono(700, 1, '64 64 64'),
        hoverOutline: mono(500, 0.8, '115 115 115'),
        hoverCell: 'rgba(37, 99, 235, 0.7)',
        selection: '#16a34a',
        handleFill: '#ffffff',
        handleStroke: '#2563eb',
        moveStroke: 'rgba(124, 58, 237, 0.9)',
        moveFill: 'rgba(124, 58, 237, 0.12)',
        resizeStroke: 'rgba(37, 99, 235, 0.9)',
        resizeFill: 'rgba(37, 99, 235, 0.12)',
        ghostAisleFill: 'rgba(37, 99, 235, 0.18)',
        ghostBlockFill: 'rgba(124, 58, 237, 0.16)',
        invalidFill: 'rgba(220, 38, 38, 0.14)',
        invalidStroke: 'rgba(220, 38, 38, 0.85)',
        block: { fill: '#dbeafe', stroke: mono(400, 1, '163 163 163'), text: '#334155' },
        fixed: { fill: '#e0e7ff', stroke: '#6366f1', text: '#3730a3' },
        status: STATUS_BLOCK_STYLES,
        heat: HEAT_BLOCK_STYLES,
        hatch: 'rgba(100, 116, 139, 0.55)',
        pulse: '#2563eb',
        route: {
            path: '#2563eb',
            approach: 'rgba(115, 115, 115, 0.9)',
            final: '#d97706',
            start: mono(600, 1, '82 82 82'),
            startFill: '#ffffff',
            stopFill: '#ffffff',
            stopStroke: mono(500, 1, '115 115 115'),
            stopText: mono(700, 1, '64 64 64'),
            stopPickedFill: '#2563eb',
            stopPickedText: '#ffffff',
            dim: mono(100, 0.75, '245 245 245'),
            dimStroke: mono(300, 1, '212 212 212'),
            dimText: mono(400, 1, '163 163 163'),
        },
    };
}

function useMapPalette() {
    const [palette, setPalette] = useState(readMapPalette);
    useEffect(() => {
        const reread = () => setPalette(readMapPalette());
        window.addEventListener('themechange', reread);
        return () => window.removeEventListener('themechange', reread);
    }, []);
    return palette;
}

/*****************************
 * Modos
 *****************************/

const MODE_RENDERERS = {
    edit: {
        editable: true,
        // Uma cor só para o bloco: não existe mais paridade (ímpar/par) a
        // distinguir — o que identifica o bloco é o rack (rótulo do corredor)
        // e o número sequencial escrito nele.
        blockStyle: (_block, params) => params.palette.block,
    },
    status: {
        editable: false,
        blockStyle: (block, params) => {
            const rack = params.racksById.get(block.rackId);
            const entry = rack ? params.statusIndex.blocks.get(BlockKey(rack.rack, block.bay)) : null;
            if (!entry) {
                return params.palette.status.none;
            }
            // Proporcional (25/08): nenhuma bloqueada = livre; TODAS = bloqueado;
            // algumas = PARCIAL. A verdade posição-a-posição fica na Visualização
            // frontal (drawer) — o bloco só sinaliza o estado dominante.
            if (Number(entry.blocked) <= 0) {
                return params.palette.status.ok;
            }
            if (Number(entry.blocked) >= Number(entry.total)) {
                return params.palette.status.blocked;
            }
            return params.palette.status.partial;
        },
    },
    /**
     * ROTA (M5-3) — o mesmo desenho do galpão, com o percurso por cima.
     *
     * O galpão inteiro fica APAGADO e só os blocos que a rota toca mantêm a
     * cor: num mapa com 20 racks coloridos, a linha da rota some no meio do
     * carnaval. Apagar o que não é rota é o que faz o percurso ser a
     * figura, e o galpão o fundo.
     */
    /**
     * HEATMAP (M9 — §8.1.3) — o mesmo desenho do galpão, colorido pela OCUPAÇÃO.
     *
     * Entra exatamente como a rota entrou no M5: um renderer novo aqui e um
     * índice novo no saco de parâmetros do `drawScene`. A base não muda, e não
     * existe um segundo mapa.
     *
     * A faixa (`heat_band`) vem PRONTA do servidor — o navegador escolhe a cor
     * da faixa, nunca o corte. E o bloco com posição bloqueada ganha HACHURA
     * (desenhada em `drawBlocks`, depois do preenchimento): o §5 quer o A5
     * visível como espaço que existe e não se usa, não apagado do mapa.
     */
    heatmap: {
        editable: false,
        blockStyle: (block, params) => {
            const rack = params.racksById.get(block.rackId);
            const entry = rack ? params.heatIndex?.get(BlockKey(rack.rack, block.bay)) : null;
            const heat = params.palette.heat;
            if (!entry) {
                return heat.NONE;
            }
            return heat[entry.heat_band] ?? heat.NONE;
        },
    },
    route: {
        editable: false,
        blockStyle: (block, params) => {
            // O bloco tocado é identificado pela CÉLULA, não pelo código: a
            // perna de 90° aponta exatamente a célula do bloco desenhada, então
            // casar por célula não reinterpreta código de endereço nenhum.
            if (params.routeCells?.has(`${block.x}:${block.y}`)) {
                return params.palette.block;
            }
            const route = params.palette.route;
            return { fill: route.dim, stroke: route.dimStroke, text: route.dimText };
        },
    },
};

/*****************************
 * Desenho (funções locais puras de canvas; cores vêm de params.palette)
 *****************************/

function areaDrawColors(area, palette) {
    const meta = MetaForArea(area);
    return palette.isDark
        ? { fill: meta.fillDark || meta.fill, stroke: meta.strokeDark || meta.stroke }
        : { fill: meta.fill, stroke: meta.stroke };
}

function drawGridLines(ctx, params) {
    const { view, palette } = params;
    const b = view.bounds;
    const cell = view.cell;

    ctx.strokeStyle = palette.gridLine;
    ctx.lineWidth = 1;

    const topY = view.offsetY - (b.y2 + 1) * cell;
    const bottomY = view.offsetY - b.y1 * cell;
    const leftX = view.offsetX + b.x1 * cell;
    const rightX = view.offsetX + (b.x2 + 1) * cell;

    for (let x = b.x1; x <= b.x2 + 1; x++) {
        const px = view.offsetX + x * cell;
        ctx.beginPath();
        ctx.moveTo(px, topY);
        ctx.lineTo(px, bottomY);
        ctx.stroke();
    }
    for (let y = b.y1; y <= b.y2 + 1; y++) {
        const py = view.offsetY - y * cell;
        ctx.beginPath();
        ctx.moveTo(leftX, py);
        ctx.lineTo(rightX, py);
        ctx.stroke();
    }

    // Réguas de coordenadas: só no editor. r5 — a moldura passou a ser a fina
    // (mesma do status, para o fit não encolher), então a régua desenha DENTRO
    // dela: fonte compacta, base colada na borda e clamp em x=1 para o rótulo
    // de 3 dígitos (grid grande) nunca sair do canvas.
    if (!params.showRulers) {
        return;
    }
    const step = cell >= 18 ? 1 : 5;
    ctx.fillStyle = palette.ruler;
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'center';
    for (let x = b.x1; x <= b.x2; x += step) {
        ctx.fillText(String(x), view.offsetX + x * cell + cell / 2, view.canvasH - view.margin + 10);
    }
    ctx.textAlign = 'left';
    const rulerRight = leftX - 3;
    for (let y = b.y1; y <= b.y2; y += step) {
        const text = String(y);
        const textX = Math.max(1, rulerRight - ctx.measureText(text).width);
        ctx.fillText(text, textX, view.offsetY - y * cell - cell / 2 + 3);
    }
}

function drawAreaFills(ctx, params) {
    for (const area of params.model.areas) {
        const colors = areaDrawColors(area, params.palette);
        const r = AreaScreenRect(params.view, area);
        ctx.fillStyle = colors.fill;
        ctx.fillRect(r.x, r.y, r.w, r.h);
        if (r.w > 60 && r.h > 30) {
            ctx.fillStyle = colors.stroke;
            ctx.font = '12px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(params.areaLabelFor(area), r.x + r.w / 2, r.y + r.h / 2);
        }
    }
}

function drawAreaBorders(ctx, params) {
    for (const area of params.model.areas) {
        const colors = areaDrawColors(area, params.palette);
        const r = AreaScreenRect(params.view, area);
        ctx.strokeStyle = colors.stroke;
        ctx.lineWidth = 2;
        ctx.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
    }
}

function drawRacks(ctx, params) {
    const { view, palette } = params;
    for (const rack of params.model.racks) {
        if (!rack.sequence.length) {
            continue;
        }
        const b = RackBounds(rack);
        const [px, py] = CellScreenRect(view, b.minX, b.maxY);
        const pw = (b.maxX - b.minX + 1) * view.cell;
        const ph = (b.maxY - b.minY + 1) * view.cell;

        ctx.fillStyle = palette.corridorFill;
        ctx.fillRect(px, py, pw, ph);
        ctx.strokeStyle = palette.corridorStroke;
        ctx.lineWidth = 1;
        ctx.strokeRect(px + 1, py + 1, pw - 2, ph - 2);

        if (view.cell >= 22) {
            ctx.fillStyle = palette.corridorText;
            ctx.font = '11px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(params.rackLabelFor(rack), px + pw / 2, py + ph / 2 + 4);
        }
    }
}

/**
 * HACHURA das posições bloqueadas (M9 — §8.1.3: "posições BLOCKED (A5)
 * hachuradas").
 *
 * Riscos a 45° POR CIMA do preenchimento, não uma cor própria: o bloco precisa
 * dizer as duas coisas ao mesmo tempo — quão cheio está a parte que se usa
 * (a cor do termômetro) E que ele tem andar que ninguém alcança (a hachura).
 * Trocar a cor apagaria a primeira informação para dar a segunda.
 */
function drawBlockedHatch(ctx, px, py, cw, ch, color) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(px + 1, py + 1, cw - 2, ch - 2);
    ctx.clip();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    const step = 5;
    for (let offset = -ch; offset < cw; offset += step) {
        ctx.beginPath();
        ctx.moveTo(px + offset, py + ch);
        ctx.lineTo(px + offset + ch, py);
        ctx.stroke();
    }
    ctx.restore();
}

function drawBlocks(ctx, params) {
    const { view } = params;
    for (const block of params.model.blocks) {
        if (!InsideView(view, block.x, block.y)) {
            continue;
        }
        const style = params.renderer.blockStyle(block, params);
        const [px, py, cw, ch] = CellScreenRect(view, block.x, block.y);
        ctx.fillStyle = style.fill;
        ctx.fillRect(px, py, cw, ch);
        const rack = params.racksById.get(block.rackId);
        const blockKey = rack ? BlockKey(rack.rack, block.bay) : null;
        if (blockKey && params.hatchedBlocks?.has(blockKey)) {
            drawBlockedHatch(ctx, px, py, cw, ch, params.palette.hatch);
        }
        ctx.strokeStyle = style.stroke;
        ctx.lineWidth = 1;
        ctx.strokeRect(px + 1, py + 1, cw - 2, ch - 2);
        // Bloco ESCOLHIDO para a contagem (§6.9): moldura grossa da cor de
        // seleção, por cima de tudo — o Supervisor precisa ver a escolha dele
        // sem perder de vista o termômetro por baixo.
        if (blockKey && params.selectedBlockKeys?.has(blockKey)) {
            ctx.strokeStyle = params.palette.selection;
            ctx.lineWidth = 3;
            ctx.strokeRect(px + 2, py + 2, cw - 4, ch - 4);
        }
        // Nº SEQUENCIAL do bloco (o rack está escrito no corredor); com célula
        // grande cabe o código bipável inteiro (`A-01`).
        if (view.cell >= 26) {
            ctx.fillStyle = style.text;
            ctx.font = '11px sans-serif';
            ctx.textAlign = 'center';
            const text =
                view.cell >= 40 && rack ? FormatBayCode(rack.rack, block.bay) : String(block.bay).padStart(2, '0');
            ctx.fillText(text, px + cw / 2, py + ch / 2 + 4);
        }
    }
}

// Quebra o code da fixa em até 2 linhas nos hífens (códigos literais são
// longos — STG-DCA-80-01 — e a célula é pequena).
function splitFixedCode(code) {
    const text = String(code || '');
    if (text.length <= 8) {
        return [text];
    }
    const parts = text.split('-');
    if (parts.length < 2) {
        return [text];
    }
    let first = parts[0];
    let index = 1;
    while (index < parts.length && `${first}-${parts[index]}`.length <= Math.ceil(text.length / 2)) {
        first = `${first}-${parts[index]}`;
        index += 1;
    }
    const second = parts.slice(index).join('-');
    return second ? [first, second] : [first];
}

function drawFixedCellContent(ctx, view, x, y, code, style, ghost = false) {
    const [px, py, cw, ch] = CellScreenRect(view, x, y);
    ctx.fillStyle = style.fill;
    ctx.fillRect(px, py, cw, ch);
    ctx.strokeStyle = style.stroke;
    ctx.lineWidth = ghost ? 2 : 1.5;
    if (ghost) {
        ctx.setLineDash([5, 3]);
    }
    ctx.strokeRect(px + 1.5, py + 1.5, cw - 3, ch - 3);
    ctx.setLineDash([]);
    if (view.cell < 20) {
        return;
    }
    const lines = splitFixedCode(code);
    const longest = Math.max(...lines.map((line) => line.length), 1);
    const fontSize = Math.max(6, Math.min(10, Math.floor((cw * 0.94) / (longest * 0.62))));
    ctx.fillStyle = style.text;
    ctx.font = `600 ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    const lineHeight = fontSize + 1;
    const startY = py + ch / 2 - ((lines.length - 1) * lineHeight) / 2 + fontSize / 3;
    lines.forEach((line, i) => {
        ctx.fillText(line, px + cw / 2, startY + i * lineHeight);
    });
}

/** Fixas: quadrado com o code curto (contrato da 4ª rodada). */
function drawFixed(ctx, params) {
    for (const item of params.model.fixed || []) {
        if (!InsideView(params.view, item.x, item.y)) {
            continue;
        }
        drawFixedCellContent(ctx, params.view, item.x, item.y, item.code, params.palette.fixed);
    }
}

// Contorno de UM elemento: bloco/fixa/célula de corredor = 1 célula; rack
// inteiro (seleção) = todas as células; área = retângulo.
function strokeElement(ctx, params, element, color, width, inset) {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    if (element.kind === 'area') {
        const area = GetAreaById(params.model, element.id);
        if (area) {
            const r = AreaScreenRect(params.view, area);
            ctx.strokeRect(r.x + inset, r.y + inset, r.w - inset * 2, r.h - inset * 2);
        }
        return;
    }
    let cells = [];
    if (element.kind === 'rack') {
        const rack = GetRackById(params.model, element.id);
        cells = rack ? GetAllRackCells(params.model, rack) : [];
    } else if (element.kind === 'rackCell') {
        cells = [{ x: element.x, y: element.y }];
    } else if (element.kind === 'block') {
        const block = GetBlockById(params.model, element.id);
        cells = block ? [{ x: block.x, y: block.y }] : [];
    } else if (element.kind === 'fixed') {
        const item = GetFixedById(params.model, element.id);
        cells = item ? [{ x: item.x, y: item.y }] : [];
    } else if (element.kind === 'cells') {
        cells = element.cells;
    }
    for (const c of cells) {
        if (!InsideView(params.view, c.x, c.y)) {
            continue;
        }
        const [px, py, cw, ch] = CellScreenRect(params.view, c.x, c.y);
        ctx.strokeRect(px + inset, py + inset, cw - inset * 2, ch - inset * 2);
    }
}

// Hover POR ELEMENTO (correção do dono): destaca APENAS o elemento da célula
// sob o cursor — bloco individual, célula de corredor, fixa; área só no edit.
function drawHoverElement(ctx, params) {
    const element = params.hoverElement;
    if (!element || params.interaction || params.paletteDrag || params.rackTrace || params.placing) {
        return;
    }
    const sel = params.selected;
    if (sel && sel.kind === element.kind && sel.id === element.id) {
        return;
    }
    strokeElement(ctx, params, element, params.palette.hoverOutline, 2, 1.5);
}

// Seleção interna do editor (área/rack/bloco/fixa por id do layout).
function drawEditSelection(ctx, params) {
    if (!params.selected) {
        return;
    }
    strokeElement(ctx, params, params.selected, params.palette.selection, 3, 2);
}

// Seleção PERSISTENTE do modo status (prop `selection`, resolvida para
// células — a seleção de rack pode contornar o rack inteiro).
function drawStatusSelection(ctx, params) {
    if (!params.statusSelectionCells || !params.statusSelectionCells.length) {
        return;
    }
    strokeElement(
        ctx,
        params,
        { kind: 'cells', cells: params.statusSelectionCells },
        params.palette.selection,
        3,
        2,
    );
}

function drawPreviewCells(ctx, params, cells, fillColor, strokeColor) {
    for (const c of cells) {
        if (!InsideView(params.view, c.x, c.y)) {
            continue;
        }
        const [px, py, cw, ch] = CellScreenRect(params.view, c.x, c.y);
        ctx.fillStyle = fillColor;
        ctx.fillRect(px, py, cw, ch);
        ctx.strokeStyle = strokeColor;
        ctx.strokeRect(px + 1, py + 1, cw - 2, ch - 2);
    }
}

function drawInteractionPreview(ctx, params) {
    const it = params.interaction;
    if (!it || !it.current) {
        return;
    }
    const { palette } = params;
    ctx.save();

    if (it.mode === 'move-area') {
        const area = GetAreaById(params.model, it.areaId);
        if (area) {
            const dx = it.current.x - it.start.x;
            const dy = it.current.y - it.start.y;
            const [px, py] = CellScreenRect(params.view, area.x1 + dx, area.y2 + dy);
            const pw = (area.x2 - area.x1 + 1) * params.view.cell;
            const ph = (area.y2 - area.y1 + 1) * params.view.cell;
            ctx.lineWidth = 3;
            ctx.setLineDash([6, 4]);
            ctx.strokeStyle = palette.moveStroke;
            ctx.strokeRect(px, py, pw, ph);
        }
    }

    if (it.mode === 'move-rack') {
        const rack = GetRackById(params.model, it.rackId);
        if (rack) {
            const dx = it.current.x - it.start.x;
            const dy = it.current.y - it.start.y;
            const cells = GetAllRackCells(params.model, rack).map((c) => ({ x: c.x + dx, y: c.y + dy }));
            drawPreviewCells(ctx, params, cells, palette.moveFill, palette.moveStroke);
        }
    }

    if (it.mode === 'move-fixed') {
        const item = GetFixedById(params.model, it.fixedId);
        if (item) {
            const dx = it.current.x - it.start.x;
            const dy = it.current.y - it.start.y;
            drawPreviewCells(ctx, params, [{ x: item.x + dx, y: item.y + dy }], palette.moveFill, palette.moveStroke);
        }
    }

    if (it.mode === 'resize-area') {
        const newRect = ComputeAreaResizeBounds(it.original, it.handle, it.current);
        const [px, py] = CellScreenRect(params.view, newRect.x1, newRect.y2);
        const pw = (newRect.x2 - newRect.x1 + 1) * params.view.cell;
        const ph = (newRect.y2 - newRect.y1 + 1) * params.view.cell;
        ctx.lineWidth = 3;
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = palette.resizeStroke;
        ctx.strokeRect(px, py, pw, ph);
    }

    if (it.mode === 'resize-rack') {
        const rack = GetRackById(params.model, it.rackId);
        if (rack) {
            const cells = ComputeRackResizePreviewCells(rack, it.current);
            drawPreviewCells(ctx, params, cells, palette.resizeFill, palette.resizeStroke);
        }
    }

    ctx.restore();
}

// Ghost do ARRASTO DA PALETA: só ÁREA arrasta (o rack é traçado de dois
// cliques, ver drawRackTrace).
function drawPaletteGhost(ctx, params) {
    const { paletteDrag, paletteHover, palette } = params;
    if (!paletteDrag || !paletteHover || paletteDrag.kind !== 'area') {
        return;
    }
    ctx.save();
    const meta = AREA_TYPES[paletteDrag.type] || AREA_TYPES.outro;
    const rect = ComputeAreaDropRect(params.model.grid, paletteHover);
    const [px, py] = CellScreenRect(params.view, rect.x1, rect.y2);
    const pw = (rect.x2 - rect.x1 + 1) * params.view.cell;
    const ph = (rect.y2 - rect.y1 + 1) * params.view.cell;
    ctx.fillStyle = palette.isDark ? meta.fillDark || meta.fill : meta.fill;
    ctx.fillRect(px, py, pw, ph);
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 2;
    ctx.strokeStyle = palette.isDark ? meta.strokeDark || meta.stroke : meta.stroke;
    ctx.strokeRect(px + 1, py + 1, pw - 2, ph - 2);
    ctx.restore();
}

// Ghost do rack ARMADO (chip "Rack" clicado, antes do 1º clique): a fatia de
// 1 célula — corredor + bloco do lado — segue o cursor, como o
// `drawStreetGhost` do protótipo do dono.
function drawRackArmedGhost(ctx, params) {
    const { rackTrace, hover, palette } = params;
    if (!rackTrace || rackTrace.start || !hover) {
        return;
    }
    const opts = rackTrace.opts;
    ctx.save();
    const cells = [{ x: hover.x, y: hover.y, kind: 'aisle' }];
    const p = SideCell(hover, opts.baySide, opts.orientation);
    if (InsideGrid(p.x, p.y, params.model.grid)) {
        cells.push({ ...p, kind: 'block' });
    }
    for (const c of cells) {
        const [px, py, cw, ch] = CellScreenRect(params.view, c.x, c.y);
        ctx.fillStyle = c.kind === 'aisle' ? palette.ghostAisleFill : palette.ghostBlockFill;
        ctx.fillRect(px, py, cw, ch);
    }
    ctx.restore();
}

// Preview AO VIVO do traçado: entre o 1º e o 2º clique ele acompanha o
// CURSOR (orientação e comprimento derivados na hora); depois do 2º clique
// fica fixo enquanto o painel de parâmetros está aberto. Corredor + blocos com
// as cores de criação; traçado INVÁLIDO pinta em vermelho — a violação aparece
// antes do "Criar". Ao posicionar um rack existente, só os vãos vivos do
// cadastro ganham bloco no preview (o resto é gap, como no desenho final).
function drawRackTrace(ctx, params) {
    const trace = params.rackTrace;
    if (!trace?.geometry) {
        return;
    }
    const { palette } = params;
    const valid = trace.valid;
    ctx.save();

    for (const [seqIndex, item] of trace.geometry.sequence.entries()) {
        const cells = [{ x: item.x, y: item.y, kind: 'aisle' }];
        if (!trace.existingBays || trace.existingBays.has(seqIndex + 1)) {
            cells.push({ ...SideCell(item, trace.geometry.baySide, trace.geometry.orientation), kind: 'block' });
        }
        for (const c of cells) {
            const [px, py, cw, ch] = CellScreenRect(params.view, c.x, c.y);
            ctx.fillStyle = valid
                ? c.kind === 'aisle'
                    ? palette.ghostAisleFill
                    : palette.ghostBlockFill
                : palette.invalidFill;
            ctx.fillRect(px, py, cw, ch);
            ctx.strokeStyle = valid ? palette.resizeStroke : palette.invalidStroke;
            ctx.lineWidth = 1;
            ctx.strokeRect(px + 1, py + 1, cw - 2, ch - 2);
        }
    }

    // Marcador do INÍCIO (a célula do 1º clique — âncora do traçado).
    if (trace.start) {
        const [sx, sy, sw, sh] = CellScreenRect(params.view, trace.start.x, trace.start.y);
        ctx.setLineDash([]);
        ctx.lineWidth = 3;
        ctx.strokeStyle = valid ? palette.resizeStroke : palette.invalidStroke;
        ctx.strokeRect(sx + 2, sy + 2, sw - 4, sh - 4);
    }

    ctx.restore();
}

// Ghost do modo POSICIONAR fixa (placeRequest): a célula sob o cursor mostra
// o code; célula inválida (ocupada/fora) pinta em vermelho.
function drawPlaceGhost(ctx, params) {
    const { placing, hover, palette } = params;
    if (!placing || !hover) {
        return;
    }
    ctx.save();
    const style = params.placeValid
        ? palette.fixed
        : { fill: palette.invalidFill, stroke: palette.invalidStroke, text: palette.invalidStroke };
    drawFixedCellContent(ctx, params.view, hover.x, hover.y, placing.code, style, true);
    ctx.restore();
}

function drawAreaHandles(ctx, params, area) {
    for (const [, x, y] of AreaHandleScreenPositions(params.view, area)) {
        ctx.fillStyle = params.palette.handleFill;
        ctx.strokeStyle = params.palette.handleStroke;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.rect(x - 5, y - 5, 10, 10);
        ctx.fill();
        ctx.stroke();
    }
}

function drawRackEndHandle(ctx, params, rack) {
    const [x, y] = RackHandleScreenPos(params.view, rack);
    const [cellX, cellY, cw, ch] = CellScreenRect(params.view, rack.end.x, rack.end.y);

    ctx.strokeStyle = params.palette.handleStroke;
    ctx.lineWidth = 4;
    ctx.strokeRect(cellX + 2, cellY + 2, cw - 4, ch - 4);

    ctx.fillStyle = params.palette.handleStroke;
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = params.palette.handleFill;
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fill();
}

// Alças SÓ na seleção (interação direta: selecionou, apareceram; bloco e fixa
// não têm alça — só arrasto).
function drawHandles(ctx, params) {
    if (!params.selected || params.interaction) {
        return;
    }
    if (params.selected.kind === 'area') {
        const area = GetAreaById(params.model, params.selected.id);
        if (area) {
            drawAreaHandles(ctx, params, area);
        }
    }
    if (params.selected.kind === 'rack') {
        const rack = GetRackById(params.model, params.selected.id);
        if (rack) {
            drawRackEndHandle(ctx, params, rack);
        }
    }
}

function drawHoverCell(ctx, params) {
    const cell = params.hover;
    if (!cell || params.interaction || params.paletteDrag) {
        return;
    }
    const [px, py, cw, ch] = CellScreenRect(params.view, cell.x, cell.y);
    ctx.strokeStyle = params.palette.hoverCell;
    ctx.lineWidth = params.rackTrace || params.placing ? 2 : 1;
    ctx.strokeRect(px + 1, py + 1, cw - 2, ch - 2);
}

// Pisca-pisca do highlight lista→mapa: contorno pulsante + halo no bloco
// desenhado que casa rack+bay.
function drawPulse(ctx, params) {
    const pulse = params.pulse;
    if (!pulse) {
        return;
    }
    const block = FindBlockByRackBay(params.model, pulse.rack, pulse.bay);
    if (!block || !InsideView(params.view, block.x, block.y)) {
        return;
    }
    const phase = (performance.now() % 700) / 700;
    const alpha = 0.35 + 0.6 * Math.abs(Math.sin(phase * Math.PI));
    const [px, py, cw, ch] = CellScreenRect(params.view, block.x, block.y);

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = params.palette.pulse;
    ctx.lineWidth = 3;
    ctx.strokeRect(px + 1.5, py + 1.5, cw - 3, ch - 3);
    ctx.globalAlpha = alpha * 0.45;
    ctx.lineWidth = 2;
    ctx.strokeRect(px - 3, py - 3, cw + 6, ch + 6);
    ctx.restore();
}

/*****************************
 * Rota SOBRE o mapa (M5-3)
 *****************************/

/**
 * Centro em px de uma coordenada de CÉLULA — inclusive fracionária.
 *
 * `CellScreenRect` só serve célula inteira (é retângulo de célula); a rota
 * chega em coordenada contínua porque o motor devolve METROS e o comparador
 * divide por `map_cell_m`. Meia célula de diferença aqui é o que separa a
 * linha passando pelo MEIO do corredor da linha passando pela borda dele.
 */
function cellCenterPx(view, x, y) {
    return [view.offsetX + (x + 0.5) * view.cell, view.offsetY - (y + 0.5) * view.cell];
}

/** A linha do percurso: a polilinha que o MOTOR calculou, em células. */
function drawRoutePath(ctx, params) {
    const points = params.route?.polyline ?? [];
    if (points.length < 2) {
        return;
    }
    ctx.save();
    ctx.strokeStyle = params.palette.route.path;
    ctx.lineWidth = Math.max(2, Math.min(5, params.view.cell * 0.14));
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    points.forEach((point, index) => {
        const [px, py] = cellCenterPx(params.view, point.x, point.y);
        if (index === 0) {
            ctx.moveTo(px, py);
        } else {
            ctx.lineTo(px, py);
        }
    });
    ctx.stroke();
    ctx.restore();
}

/**
 * As pernas de 90°: o tracinho que sai do corredor e aponta a CÉLULA DO BLOCO.
 * É a informação que o protótipo do dono não tinha ("a rota parava no meio da
 * rua sem indicar o lado"). A ÚLTIMA vem grossa e com ponta de seta, porque é
 * a que responde "ao chegar, para que lado eu viro".
 */
function drawRouteApproachLegs(ctx, params) {
    const route = params.route;
    if (!route) {
        return;
    }
    const finalLeg = route.final_leg ?? null;
    ctx.save();
    for (const [index, leg] of (route.approach_legs ?? []).entries()) {
        const isFinal = Boolean(finalLeg) && index === route.approach_legs.length - 1 && leg.code === finalLeg.code;
        const [fx, fy] = cellCenterPx(params.view, leg.from.x, leg.from.y);
        const [tx, ty] = cellCenterPx(params.view, leg.to.x, leg.to.y);
        ctx.strokeStyle = isFinal ? params.palette.route.final : params.palette.route.approach;
        ctx.lineWidth = isFinal ? Math.max(3, params.view.cell * 0.16) : Math.max(1.5, params.view.cell * 0.08);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(fx, fy);
        ctx.lineTo(tx, ty);
        ctx.stroke();
        if (!isFinal) {
            continue;
        }
        // Ponta de seta na perna final (o canvas não tem marker de SVG).
        const angle = Math.atan2(ty - fy, tx - fx);
        const size = Math.max(6, params.view.cell * 0.3);
        ctx.fillStyle = params.palette.route.final;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(tx - size * Math.cos(angle - 0.5), ty - size * Math.sin(angle - 0.5));
        ctx.lineTo(tx - size * Math.cos(angle + 0.5), ty - size * Math.sin(angle + 0.5));
        ctx.closePath();
        ctx.fill();
    }
    ctx.restore();
}

/** Onde a rota COMEÇA: o `last_position` do equipamento (§6.4). */
function drawRouteStart(ctx, params) {
    const start = params.route?.start;
    if (!start) {
        return;
    }
    const [px, py] = cellCenterPx(params.view, start.cell.x, start.cell.y);
    const size = Math.max(7, params.view.cell * 0.34);
    ctx.save();
    ctx.fillStyle = params.palette.route.startFill;
    ctx.strokeStyle = params.palette.route.start;
    ctx.lineWidth = 2;
    ctx.fillRect(px - size / 2, py - size / 2, size, size);
    ctx.strokeRect(px - size / 2, py - size / 2, size, size);
    ctx.restore();
}

/**
 * Posição de tela de cada parada, com desempate quando DUAS caem na mesma
 * célula. Isso acontece de verdade na jornada nova: as pernas 1 e 3 de um
 * mesmo pallet no mesmo rack param no MESMO ponto do corredor. Dois círculos
 * sobrepostos esconderiam um item — e no modo manual esconderiam um alvo
 * clicável.
 */
function routeStopPositions(params) {
    const stops = params.route?.stops ?? [];
    const groups = new Map();
    for (const stop of stops) {
        const key = `${Math.round(stop.cell.x)}:${Math.round(stop.cell.y)}`;
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key).push(stop);
    }
    const out = [];
    for (const siblings of groups.values()) {
        siblings.forEach((stop, index) => {
            const [px, py] = cellCenterPx(params.view, stop.cell.x, stop.cell.y);
            const spread = siblings.length > 1 ? (index - (siblings.length - 1) / 2) * (params.view.cell * 0.42) : 0;
            out.push({ stop, x: px + spread, y: py - spread * 0.35 });
        });
    }
    return out;
}

/** Raio do alvo de uma parada (também é a área de clique do modo manual). */
function routeStopRadius(params) {
    return Math.max(9, Math.min(16, params.view.cell * 0.38));
}

/**
 * As PARADAS numeradas. O número é a ordem da rota em foco — ou a ordem que o
 * Supervisor está montando no modo manual. Parada ainda sem número aparece
 * como alvo vazio: é o que sobra para ele tocar.
 */
function drawRouteStops(ctx, params) {
    if (!params.route) {
        return;
    }
    const radius = routeStopRadius(params);
    ctx.save();
    ctx.textAlign = 'center';
    for (const { stop, x, y } of params.routeStopPositions ?? []) {
        const picked = stop.order !== null && stop.order !== undefined;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fillStyle = picked ? params.palette.route.stopPickedFill : params.palette.route.stopFill;
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = picked ? params.palette.route.stopPickedFill : params.palette.route.stopStroke;
        ctx.stroke();
        ctx.fillStyle = picked ? params.palette.route.stopPickedText : params.palette.route.stopText;
        ctx.font = `600 ${Math.max(9, Math.round(radius * 0.95))}px sans-serif`;
        ctx.fillText(picked ? String(stop.order) : '·', x, y + radius * 0.36);
    }
    ctx.restore();
}

// Executa `fn` recortado aos bounds da view (filtro de área ativo): nada
// vaza para as réguas/margens quando um elemento fica meio para fora.
function withViewClip(ctx, params, fn) {
    if (!params.clipToBounds) {
        fn();
        return;
    }
    const b = params.view.bounds;
    const [px, py] = CellScreenRect(params.view, b.x1, b.y2);
    ctx.save();
    ctx.beginPath();
    ctx.rect(px, py, (b.x2 - b.x1 + 1) * params.view.cell, (b.y2 - b.y1 + 1) * params.view.cell);
    ctx.clip();
    fn();
    ctx.restore();
}

function drawScene(canvas, params) {
    const { view } = params;
    const dpr = window.devicePixelRatio || 1;

    canvas.style.width = `${view.canvasW}px`;
    canvas.style.height = `${view.canvasH}px`;
    const bitmapW = Math.floor(view.canvasW * dpr);
    const bitmapH = Math.floor(view.canvasH * dpr);
    if (canvas.width !== bitmapW) {
        canvas.width = bitmapW;
    }
    if (canvas.height !== bitmapH) {
        canvas.height = bitmapH;
    }

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, view.canvasW, view.canvasH);
    ctx.fillStyle = params.palette.surface;
    ctx.fillRect(0, 0, view.canvasW, view.canvasH);

    withViewClip(ctx, params, () => {
        drawAreaFills(ctx, params);
        drawRacks(ctx, params);
        drawBlocks(ctx, params);
        drawFixed(ctx, params);
    });

    drawGridLines(ctx, params);

    withViewClip(ctx, params, () => {
        drawAreaBorders(ctx, params);

        drawHoverElement(ctx, params);
        if (params.editLayers) {
            drawEditSelection(ctx, params);
            drawInteractionPreview(ctx, params);
            drawPaletteGhost(ctx, params);
        } else {
            drawStatusSelection(ctx, params);
        }
        drawRackArmedGhost(ctx, params);
        drawRackTrace(ctx, params);
        drawPlaceGhost(ctx, params);
        if (params.editLayers) {
            drawHandles(ctx, params);
        }
        // A rota vai POR CIMA de tudo: ela é a figura, o galpão é o fundo.
        if (params.route) {
            drawRoutePath(ctx, params);
            drawRouteApproachLegs(ctx, params);
            drawRouteStart(ctx, params);
            drawRouteStops(ctx, params);
        }
        drawPulse(ctx, params);
        if (params.editLayers || params.rackTrace || params.placing) {
            drawHoverCell(ctx, params);
        }
    });
}

/*****************************
 * Componente
 *****************************/

const NATURAL_WRAP_CLASS =
    'relative max-h-[70vh] overflow-auto rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900';
// `w-full min-w-0` é ESTRUTURAL, não estética: o canvas é dimensionado a
// partir da largura MEDIDA deste wrap. Sem largura própria, o wrap (item flex)
// encolhe até o conteúdo — e o conteúdo é o canvas que veio da medida
// anterior. Isso realimenta e trava o mapa pequeno para sempre (bug real: 268px
// num monitor Full HD). Com `w-full`, a medida é a da COLUNA, independente do
// canvas, e o desenho fica centrado dentro dela.
const FIT_WRAP_CLASS =
    'flex w-full min-w-0 items-center justify-center overflow-hidden rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900';
const AREA_WRAP_CLASS =
    'flex h-[60vh] max-h-[640px] w-full min-w-0 items-center justify-center overflow-hidden rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900';

const WarehouseMap = ({
    mode = 'status',
    layout,
    onLayoutChange,
    overlay = [],
    areaFilter = null,
    selection = null,
    onSelect,
    fitHeight = null,
    onRackCreated,
    placeRequest = null,
    onPlaceResolved,
    highlight = null,
    route = null,
    onRouteStopClick = null,
    // M9 — o termômetro de ocupação: `[{rack, bay, heat_band, has_blocked,
    // occupancy_pct, ...}]` vindo de GET /warehouse/inventory/occupancy. É o
    // mesmo desenho, com um índice a mais — como o M5 fez com a rota.
    heatmap = null,
    // Blocos escolhidos para a OS de CONTAGEM (§6.9): `["A:2", ...]` (BlockKey).
    selectedBlocks = null,
}) => {
    const { label } = useWarehouseLabels();
    const palette = useMapPalette();
    const renderer = MODE_RENDERERS[mode] || MODE_RENDERERS.status;
    const editing = Boolean(renderer.editable);

    const baseModel = useMemo(() => NormalizeLayout(layout) || DefaultLayout(), [layout]);

    // Rascunho local: no edit é SEMPRE o modelo de trabalho (Salvar/Descartar);
    // no status só existe enquanto um PUT pendente precisa de retry (banner).
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [banner, setBanner] = useState(false);
    const [bannerBusy, setBannerBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [passwordOpen, setPasswordOpen] = useState(false);
    // Exclusão/adição de rack ou bloco em andamento (trava os botões das fichas).
    const [rackOpBusy, setRackOpBusy] = useState(false);

    const [selected, setSelected] = useState(null);
    const [interaction, setInteraction] = useState(null);
    const [paletteDrag, setPaletteDrag] = useState(null);
    const [paletteHover, setPaletteHover] = useState(null);
    const [dragPoint, setDragPoint] = useState(null);
    /**
     * Traçado de rack — UM estado para as três fases do gesto:
     *   {key, start: null, end: null}  → ARMADO (chip "Rack" clicado): o ghost
     *                                    de 1 célula segue o cursor;
     *   {key, start, end: null}        → 1º clique dado: o corredor segue o
     *                                    cursor (orientação/comprimento
     *                                    derivados na hora);
     *   {key, start, end}              → 2º clique dado: painel de parâmetros
     *                                    aberto e traçado fixo.
     * `opts` sobrevive ao re-traçado (clicar de novo no mapa não perde o que
     * já foi digitado); ESC (ou Cancelar) mata o gesto inteiro. O CONFIRMAR
     * chama o generate-rack real antes de desenhar — salvo `placeOnly`
     * (posicionar um rack que JÁ existe no cadastro: só desenho, com
     * `existingBays` dizendo quais vãos ganham bloco).
     */
    const [rackDraft, setRackDraft] = useState(null);
    const [draftApiError, setDraftApiError] = useState(null);
    const [creatingRack, setCreatingRack] = useState(false);
    // Modo posicionar fixa (placeRequest {type:'fixed'}): {code}.
    const [placing, setPlacing] = useState(null);
    const [placingBusy, setPlacingBusy] = useState(false);

    const [hover, setHover] = useState(null);
    const [hoverElement, setHoverElement] = useState(null);
    const [cursor, setCursor] = useState('default');
    const [statusMsg, setStatusMsg] = useState(null);
    const [areaModalId, setAreaModalId] = useState(null);
    const [rackModalId, setRackModalId] = useState(null);
    const [blockModalId, setBlockModalId] = useState(null);
    // Gap do corredor clicado → confirmar "Adicionar bloco N": {rackId, rack, bay}.
    const [blockAddTarget, setBlockAddTarget] = useState(null);
    // Gravação da largura no CADASTRO (chamada de rede dentro do modal de
    // bloco): o botão precisa dizer que está gravando algo real.
    const [savingBlock, setSavingBlock] = useState(false);
    const [gridDraft, setGridDraft] = useState({ w: '30', h: '30', cell: '28' });
    const [wrapSize, setWrapSize] = useState({ w: 800, h: 600 });
    // Pisca do highlight: {rack, bay, until}; o rAF força re-render por
    // ~2s (o redraw do canvas é barato nessa escala).
    const [pulse, setPulse] = useState(null);
    const [, setPulseFrame] = useState(0);

    const canvasRef = useRef(null);
    const wrapRef = useRef(null);
    const statusTimerRef = useRef(null);
    // Ação a rodar DEPOIS da senha confirmada (salvar OU excluir rack/bloco OU
    // adicionar bloco): o ConfirmPasswordModal é um só, o que muda é o que ele
    // dispara. Ref (não state) para não recriar o handler e não vazar closure.
    const passwordActionRef = useRef(null);
    const dirtyRef = useRef(false);
    dirtyRef.current = dirty;
    const bannerRef = useRef(false);
    bannerRef.current = banner;

    const model = draft || baseModel;

    // Sincroniza o rascunho com o layout salvo: entrar no edit copia o salvo;
    // layout novo do wire substitui o rascunho quando NÃO há edição pendente
    // (dirty) nem PUT pendente (banner).
    useEffect(() => {
        if (editing) {
            setDraft((previous) => (previous && (dirtyRef.current || bannerRef.current) ? previous : baseModel));
        } else {
            setDraft((previous) => (previous && bannerRef.current ? previous : null));
            setDirty(false);
        }
    }, [editing, baseModel]);

    // Troca de modo limpa os estados de gesto do editor.
    useEffect(() => {
        setSelected(null);
        setInteraction(null);
        setPaletteDrag(null);
        setPaletteHover(null);
        setDragPoint(null);
        setRackDraft(null);
        setDraftApiError(null);
        setAreaModalId(null);
        setRackModalId(null);
        setBlockModalId(null);
        setBlockAddTarget(null);
    }, [editing]);

    /*****************************
     * Filtro de área (recorte VISUAL — edição continua no mundo inteiro)
     *****************************/

    const filterArea = useMemo(() => {
        if (areaFilter === null || areaFilter === undefined || areaFilter === '') {
            return null;
        }
        return model.areas.find((area) => String(area?.id) === String(areaFilter)) || null;
    }, [model, areaFilter]);

    // FilterLayoutByArea é do módulo da página (areaFilter.js) — reusado aqui;
    // o ramo `fixed` (que ele preserva inteiro) é recortado pelas áreas mantidas.
    const displayModel = useMemo(() => {
        if (!filterArea) {
            return model;
        }
        const filtered = FilterLayoutByArea(model, filterArea.id);
        const keptAreas = Array.isArray(filtered.areas) ? filtered.areas : [];
        const fixed = (model.fixed || []).filter((item) =>
            keptAreas.some((area) => PointInsideRect({ x: item.x, y: item.y }, area)),
        );
        return { ...filtered, fixed };
    }, [model, filterArea]);

    const maps = useMemo(() => BuildCellMaps(displayModel), [displayModel]);
    const statusIndex = useMemo(() => BuildStatusOverlayIndex(overlay), [overlay]);
    // Células de bloco que a rota TOCA: é o que continua colorido enquanto o
    // resto do galpão fica apagado.
    const routeCells = useMemo(() => {
        const cells = new Set();
        for (const cell of route?.block_cells ?? []) {
            cells.add(`${Math.round(cell.x)}:${Math.round(cell.y)}`);
        }
        return cells;
    }, [route]);
    // Índice do heatmap por BlockKey(rack, bay) — a MESMA chave do overlay de
    // status, porque é a mesma pergunta ("qual bloco do desenho é esta linha?").
    const heatIndex = useMemo(() => {
        if (!heatmap) {
            return null;
        }
        return new Map(heatmap.map((entry) => [BlockKey(entry.rack, entry.bay), entry]));
    }, [heatmap]);
    // Blocos com posição bloqueada — o que ganha hachura (§8.1.3).
    const hatchedBlocks = useMemo(() => {
        const keys = new Set();
        for (const entry of heatmap ?? []) {
            if (entry.has_blocked) {
                keys.add(BlockKey(entry.rack, entry.bay));
            }
        }
        return keys;
    }, [heatmap]);
    const selectedBlockKeys = useMemo(() => new Set(selectedBlocks ?? []), [selectedBlocks]);
    const racksById = useMemo(() => new Map(displayModel.racks.map((rack) => [rack.id, rack])), [displayModel]);
    // Racks que EXISTEM no cadastro (chegam pelo overlay do GET /locations/map)
    // e ainda não foram desenhados: a paleta oferece posicioná-los.
    const unplacedRacks = useMemo(
        () =>
            [...statusIndex.racks.keys()].filter((prefix) => !FindRackByPrefix(model, prefix)).sort((a, b) => a.localeCompare(b)),
        [statusIndex, model],
    );

    const fitPx = Number.isFinite(fitHeight) && fitHeight > 0 ? fitHeight : null;

    const view = useMemo(() => {
        // MESMA moldura nos dois modos: o fit do edit é o do status (r5).
        const margin = MAP_FRAME;
        const grid = displayModel.grid;
        const bounds = filterArea
            ? { x1: filterArea.x1, y1: filterArea.y1, x2: filterArea.x2, y2: filterArea.y2 }
            : { x1: 0, y1: 0, x2: grid.w - 1, y2: grid.h - 1 };
        if (fitPx) {
            return ComputeViewTransform(grid, bounds, wrapSize.w, fitPx, margin);
        }
        if (filterArea) {
            return ComputeViewTransform(grid, bounds, wrapSize.w, wrapSize.h, margin);
        }
        return ComputeViewTransform(grid, null, wrapSize.w, wrapSize.h, margin);
    }, [displayModel.grid, filterArea, wrapSize, fitPx]);

    // Ponta do traçado: enquanto o 2º clique não vem, é o CURSOR (preview ao
    // vivo); depois, a célula clicada.
    const traceEnd = rackDraft?.start ? rackDraft.end || hover : null;

    // Preview do traçado: simula a criação no modelo real (mundo INTEIRO,
    // mesmo com filtro de área) — o erro aparece AO VIVO (vermelho no canvas,
    // texto no painel). Memo por CÉLULA: só recalcula quando o cursor troca de
    // célula, não a cada pixel.
    const rackDraftPreview = useMemo(() => {
        if (!rackDraft?.start || !traceEnd) {
            return null;
        }
        return {
            axis: DeriveRackAxis(rackDraft.start, traceEnd),
            geometry: ComputeRackGeometry(rackDraft.start, traceEnd, rackDraft.opts),
            result: CreateRackInLayout(model, rackDraft.start, traceEnd, rackDraft.opts, {
                existingBays: rackDraft.existingBays ?? null,
            }),
        };
    }, [rackDraft, traceEnd, model]);

    // Orientação DERIVADA que o painel usa para oferecer o lado certo dos
    // blocos; antes do traçado vale a semente dos defaults.
    const draftOrientation = rackDraftPreview?.axis?.orientation || rackDraft?.opts?.orientation || null;

    // Validade do ghost do modo posicionar (dry-run no modelo inteiro).
    const placeValid = useMemo(() => {
        if (!placing || !hover) {
            return false;
        }
        return CreateFixedInLayout(model, hover, placing.code).ok;
    }, [placing, hover, model]);

    // Seleção persistente do status resolvida para células do desenho.
    const statusSelectionCells = useMemo(() => {
        if (editing || !selection) {
            return null;
        }
        if (selection.kind === 'block') {
            const block = FindBlockByRackBay(displayModel, selection.rack, selection.bay);
            return block ? [{ x: block.x, y: block.y }] : null;
        }
        if (selection.kind === 'rack') {
            const rack = FindRackByPrefix(displayModel, selection.rack);
            return rack ? GetAllRackCells(displayModel, rack) : null;
        }
        if (selection.kind === 'fixed') {
            const item = FindFixedByCode(displayModel, selection.code);
            return item ? [{ x: item.x, y: item.y }] : null;
        }
        return null;
    }, [editing, selection, displayModel]);

    // Grid draft espelha o modelo (inputs commitam no blur/Enter).
    useEffect(() => {
        setGridDraft({ w: String(model.grid.w), h: String(model.grid.h), cell: String(model.grid.cell) });
    }, [model.grid.w, model.grid.h, model.grid.cell]);

    // Medida do container em px. É a LARGURA medida aqui (wrapSize.w) que dá a
    // ESCALA do mapa nos dois modos (via ComputeViewTransform), então ela tem
    // que ser ROBUSTA — senão o mapa "trava pequeno".
    const measureWrap = useCallback(() => {
        const node = wrapRef.current;
        if (!node) {
            return;
        }
        const w = node.clientWidth;
        const h = node.clientHeight;
        // Medida ZERO = elemento oculto/desmontando (troca de modo da página,
        // aba em background). Ignorar: adotar 0 encolheria o canvas ao mínimo e
        // — como a medida seguinte parte dele — o mapa nunca mais voltaria ao
        // certo (o default {800} fica até vir uma medida REAL, corrigida no burst).
        if (w <= 0 || h <= 0) {
            return;
        }
        setWrapSize((previous) => (previous.w === w && previous.h === h ? previous : { w, h }));
    }, []);

    // Burst de medida: mede JÁ e re-mede nos 2 frames seguintes. No
    // MOUNT-EM-EDIÇÃO (o dono tem localizações mas nenhum layout salvo → o status
    // mostra o placeholder e o mapa só MONTA ao ENTRAR na edição, junto da
    // toolbar/paleta) o 1º commit pode não ter assentado a largura do container:
    // a medida síncrona pega um tamanho transitório pequeno (ou fica no default
    // {800}) e TRAVARIA pequeno. Os frames seguintes pegam a largura DEFINITIVA —
    // o mapa fica do MESMO tamanho do modo status. Devolve um cancelador.
    const scheduleMeasureBurst = useCallback(() => {
        measureWrap();
        if (typeof requestAnimationFrame !== 'function') {
            return () => {};
        }
        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            measureWrap();
            raf2 = requestAnimationFrame(measureWrap);
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2) {
                cancelAnimationFrame(raf2);
            }
        };
    }, [measureWrap]);

    // Setup ÚNICO: ResizeObserver + resize da janela (mudanças CONTÍNUAS de
    // tamanho) mais o burst inicial. useLayoutEffect evita o flash do 1º paint.
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) {
            return undefined;
        }
        const cancelBurst = scheduleMeasureBurst();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureWrap) : null;
        observer?.observe(el);
        window.addEventListener('resize', measureWrap);
        return () => {
            cancelBurst();
            observer?.disconnect();
            window.removeEventListener('resize', measureWrap);
        };
    }, [measureWrap, scheduleMeasureBurst]);

    // Re-mede em BURST quando o MODO (status↔edição) ou o fitHeight muda: a troca
    // de modo pode redimensionar o container e o 1º commit do novo modo nem
    // sempre assenta a largura na hora. `mode`/`fitHeight` são o GATILHO (lidos
    // pelo ComputeViewTransform, não dentro do efeito).
    useLayoutEffect(() => {
        const cancelBurst = scheduleMeasureBurst();
        return cancelBurst;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, fitHeight, scheduleMeasureBurst]);

    useEffect(() => () => clearTimeout(statusTimerRef.current), []);

    // Highlight lista→mapa: cada mudança de pulseKey re-arma o pisca de ~2s.
    const pulseKey = highlight?.pulseKey;
    useEffect(() => {
        if (pulseKey === undefined || pulseKey === null) {
            return;
        }
        const rack = NormalizeRackPrefix(highlight?.rack);
        const bay = Number(highlight?.bay);
        if (!rack || !Number.isFinite(bay)) {
            return;
        }
        setPulse({ rack, bay, until: performance.now() + PULSE_MS });
        // `highlight.rack/bay` são lidos NO DISPARO; o gatilho do contrato
        // é exclusivamente pulseKey.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pulseKey]);

    useEffect(() => {
        if (!pulse) {
            return undefined;
        }
        let raf = 0;
        const tick = () => {
            if (performance.now() >= pulse.until) {
                setPulse(null);
                return;
            }
            setPulseFrame((frame) => frame + 1);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [pulse]);

    // Redesenha a cena a cada render (idempotente; o estado é a fonte).
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        drawScene(canvas, {
            view,
            model: displayModel,
            maps,
            renderer,
            palette,
            statusIndex,
            racksById,
            editLayers: editing,
            showRulers: editing,
            clipToBounds: Boolean(filterArea),
            selected: editing ? selected : null,
            statusSelectionCells,
            hover,
            hoverElement,
            interaction,
            paletteDrag,
            paletteHover,
            placing,
            placeValid,
            rackTrace: rackDraft
                ? {
                      start: rackDraft.start,
                      opts: rackDraft.opts,
                      existingBays: rackDraft.existingBays ?? null,
                      geometry: rackDraftPreview?.geometry || null,
                      valid: Boolean(rackDraftPreview?.result?.ok) && !draftApiError,
                  }
                : null,
            pulse,
            route,
            routeCells,
            heatIndex,
            hatchedBlocks,
            selectedBlockKeys,
            routeStopPositions: route ? routeStopPositions({ view, route }) : null,
            areaLabelFor: (area) => area?.label || areaTypeLabel(area?.type),
            rackLabelFor: (rack) => rack.name || `Rack ${rack.rack}`,
        });
    });

    /**
     * Parada da rota sob o ponteiro (px do canvas) — o alvo do modo manual.
     * O acerto é pelo CÍRCULO desenhado, não pela célula: paradas que dividem
     * a mesma célula são desempatadas no desenho e precisam ser desempatadas
     * no clique também, senão o Supervisor tocaria sempre a mesma.
     */
    const routeStopAt = (px, py) => {
        if (!route) {
            return null;
        }
        const radius = routeStopRadius({ view });
        let best = null;
        for (const entry of routeStopPositions({ view, route })) {
            const distance = Math.hypot(px - entry.x, py - entry.y);
            if (distance <= radius && (!best || distance < best.distance)) {
                best = { stop: entry.stop, distance };
            }
        }
        return best?.stop ?? null;
    };

    function areaTypeLabel(type) {
        return label(`wms.map.area_type.${AREA_TYPES[type] ? type : 'outro'}`);
    }

    const showStatus = (text, isError = false) => {
        setStatusMsg(text ? { text, isError } : null);
        clearTimeout(statusTimerRef.current);
        if (text) {
            statusTimerRef.current = setTimeout(() => setStatusMsg(null), 7000);
        }
    };

    // Snapshot/rollback: a operação já validou o mundo; falha = modelo antigo
    // + mensagem (nunca commita estado inválido). Só o EDIT muta via runOp.
    const runOp = (result, successMessage) => {
        if (!result.ok) {
            showStatus(result.error, true);
            return false;
        }
        setDraft(result.model);
        setDirty(true);
        if (result.warning) {
            showStatus(result.warning, true);
        } else {
            showStatus(successMessage);
        }
        if (result.selection) {
            setSelected(result.selection);
        }
        return true;
    };

    /*****************************
     * Persistência (Salvar com re-senha / banner de retry / descartar)
     *****************************/

    // O PUT do JSON inteiro — a ação de senha PADRÃO (Salvar).
    const performSave = async () => {
        setSaving(true);
        try {
            await api.putWarehouseMapLayout(model);
            setDirty(false);
            setBanner(false);
            onLayoutChange?.(model);
            showStatus(label('wms.map.saved'));
        } catch (error) {
            showStatus(error?.message || label('wms.common.load_error'), true);
        } finally {
            setSaving(false);
        }
    };

    const requestSave = () => {
        const worldError = ValidateWorld(model);
        if (worldError) {
            showStatus(worldError, true);
            return;
        }
        passwordActionRef.current = performSave;
        setPasswordOpen(true);
    };

    // Senha confirmada pelo backend — dispara a ação ARMADA (salvar OU excluir
    // rack/bloco OU adicionar bloco). Modal de senha é uma só; muda o que ela roda.
    const handlePasswordConfirmed = async () => {
        setPasswordOpen(false);
        const action = passwordActionRef.current;
        passwordActionRef.current = null;
        if (action) {
            await action();
        }
    };

    /*****************************
     * Exclusão de RACK no editor (senha → backend soft-delete → tira do desenho)
     *****************************/

    // "Excluir rack" na ficha do rack → fecha a ficha, arma a exclusão e pede
    // senha. A exclusão real roda só depois do OK da senha (handlePasswordConfirmed).
    const requestDeleteRack = (rackId) => {
        if (!GetRackById(model, rackId)) {
            return;
        }
        setRackModalId(null);
        passwordActionRef.current = () => performDeleteRack(rackId);
        setPasswordOpen(true);
    };

    // 200/404 → o rack some do desenho E do drawer NA HORA e o layout é
    // persistido (PUT); 404 é AUTO-CURA (o rack já não existe — sem erro
    // assustador); 409 → "ocupado, esvazie" com os ocupantes que o backend
    // lista; qualquer outro → mensagem genérica. NUNCA mistura o 404 sob o 409.
    const performDeleteRack = async (rackId) => {
        const rack = GetRackById(model, rackId);
        if (!rack) {
            return;
        }
        setRackOpBusy(true);
        let alreadyGone = false;
        try {
            await api.deleteWarehouseRack(rack.rack);
        } catch (error) {
            if (error?.status === 404) {
                alreadyGone = true; // auto-cura: cai fora do catch e limpa o desenho
            } else if (error?.status === 409) {
                setRackOpBusy(false);
                showStatus(error?.message || label('wms.map.delete_occupied'), true);
                return;
            } else {
                setRackOpBusy(false);
                showStatus(error?.message || label('wms.common.load_error'), true);
                return;
            }
        }
        // 200 ou 404: tira do desenho + drawer e persiste.
        const next = RemoveRackFromLayout(model, rackId);
        setDraft(next);
        setSelected(null);
        try {
            await api.putWarehouseMapLayout(next);
            setDirty(false);
            setBanner(false);
            onLayoutChange?.(next);
            showStatus(alreadyGone ? label('wms.map.rack_already_gone') : label('wms.map.rack_deleted'));
        } catch {
            // Dado real já apagado; só o desenho ficou por salvar (banner de retry).
            setDirty(true);
            setBanner(true);
        } finally {
            setRackOpBusy(false);
        }
    };

    /*****************************
     * Editar UM BLOCO individual: excluir (ficha do bloco) / adicionar num GAP
     * do corredor (clique numa célula vazia). Mesma disciplina: senha → backend
     * → desenho. O nº do bloco é o ordinal no corredor — o mesmo do backend.
     *****************************/

    const requestDeleteBlock = (blockId) => {
        if (!GetBlockById(model, blockId)) {
            return;
        }
        setBlockModalId(null);
        passwordActionRef.current = () => performDeleteBlock(blockId);
        setPasswordOpen(true);
    };

    // Excluir bloco: 200/404 → some do desenho (vira gap) e persiste (404 =
    // auto-cura); 409 → "ocupado, esvazie"; outro → genérico. STG e outros blocos ficam.
    const performDeleteBlock = async (blockId) => {
        const block = GetBlockById(model, blockId);
        if (!block) {
            return;
        }
        const rack = GetRackById(model, block.rackId);
        if (!rack) {
            return;
        }
        setRackOpBusy(true);
        let alreadyGone = false;
        try {
            await api.deleteWarehouseRackBay(rack.rack, block.bay);
        } catch (error) {
            if (error?.status === 404) {
                alreadyGone = true;
            } else if (error?.status === 409) {
                setRackOpBusy(false);
                showStatus(error?.message || label('wms.map.delete_occupied'), true);
                return;
            } else {
                setRackOpBusy(false);
                showStatus(error?.message || label('wms.common.load_error'), true);
                return;
            }
        }
        const next = RemoveBlockFromLayout(model, blockId);
        setDraft(next);
        setSelected(null);
        try {
            await api.putWarehouseMapLayout(next);
            setDirty(false);
            setBanner(false);
            onLayoutChange?.(next);
            showStatus(alreadyGone ? label('wms.map.block_already_gone') : label('wms.map.block_deleted'));
        } catch {
            setDirty(true);
            setBanner(true);
        } finally {
            setRackOpBusy(false);
        }
    };

    const requestAddBlock = (rackId, bay) => {
        setBlockAddTarget(null);
        if (!GetRackById(model, rackId)) {
            return;
        }
        passwordActionRef.current = () => performAddBlock(rackId, bay);
        setPasswordOpen(true);
    };

    // Adicionar bloco: desenha PRIMEIRO (valida grid/colisão); backend gera SÓ
    // aquele bloco (ressuscita se estava soft-deletado; andares/largura vêm dos
    // params do rack); depois persiste. 409 (já existe) e erros ficam na status.
    const performAddBlock = async (rackId, bay) => {
        const rack = GetRackById(model, rackId);
        if (!rack) {
            return;
        }
        const drawn = AddBlockToLayout(model, rackId, bay);
        if (!drawn.ok) {
            showStatus(drawn.error, true);
            return;
        }
        const payload = {
            levels: rack.params?.levels ?? RACK_DRAFT_DEFAULTS.levels,
            bay_width: rack.params?.blockWidth ?? RACK_DRAFT_DEFAULTS.blockWidth,
        };
        setRackOpBusy(true);
        try {
            await api.addWarehouseRackBay(rack.rack, bay, payload);
        } catch (error) {
            setRackOpBusy(false);
            if (error?.status === 409) {
                showStatus(error?.message || label('wms.map.block_exists'), true);
            } else {
                showStatus(error?.message || label('wms.common.load_error'), true);
            }
            return;
        }
        setDraft(drawn.model);
        setSelected(drawn.selection || null);
        try {
            await api.putWarehouseMapLayout(drawn.model);
            setDirty(false);
            setBanner(false);
            onLayoutChange?.(drawn.model);
            showStatus(label('wms.map.block_added'));
        } catch {
            setDirty(true);
            setBanner(true);
        } finally {
            setRackOpBusy(false);
        }
    };

    const discardChanges = () => {
        setDraft(baseModel);
        setDirty(false);
        setBanner(false);
        setSelected(null);
        setInteraction(null);
        setRackDraft(null);
        setDraftApiError(null);
        showStatus(label('wms.map.discarded'));
    };

    // Retry do PUT que falhou depois de um generate-rack bem-sucedido: o
    // dado real existe; só o desenho está por salvar.
    const retryLayoutSave = async () => {
        setBannerBusy(true);
        try {
            await api.putWarehouseMapLayout(model);
            setBanner(false);
            setDirty(false);
            onLayoutChange?.(model);
            showStatus(label('wms.map.saved'));
        } catch (error) {
            showStatus(error?.message || label('wms.common.load_error'), true);
        } finally {
            setBannerBusy(false);
        }
    };

    /*****************************
     * Traçado de rack (2 cliques) → generate-rack REAL + PUT do layout
     *****************************/

    // Arma o gesto: o próximo clique no mapa é o INÍCIO do rack. Com `placeOnly`
    // (prefixo de um rack que JÁ existe no cadastro) não há geração: o prefixo e
    // o nº de blocos vêm do dado real, e só os vãos vivos ganham bloco.
    const armRackTrace = (fromPlace = false, placeOnly = null) => {
        setSelected(null);
        setInteraction(null);
        setPaletteDrag(null);
        setPlacing(null);
        setDraftApiError(null);
        const registry = placeOnly ? statusIndex.racks.get(placeOnly) : null;
        const existingBays = registry ? new Set(registry.bays) : null;
        const bays = registry && registry.bays.length ? Math.max(...registry.bays) : null;
        // Andares do desenho de um rack existente: estimados pelo maior nº de
        // posições de um vão (2 por andar) — é só desenho; o dado real manda.
        const levelsGuess = registry
            ? Math.min(
                  RACK_REAL_MAX_LEVELS,
                  Math.max(
                      1,
                      Math.round(
                          Math.max(
                              ...registry.bays.map((bay) => Number(statusIndex.blocks.get(BlockKey(placeOnly, bay))?.total) || 0),
                          ) / RACK_DRAFT_DEFAULTS.positions,
                      ),
                  ),
              )
            : RACK_DRAFT_DEFAULTS.levels;
        setRackDraft({
            key: Date.now(),
            start: null,
            end: null,
            opts: {
                ...RACK_DRAFT_DEFAULTS,
                rack: placeOnly || NextFreeRackPrefix(model, [...statusIndex.racks.keys()]) || '',
                bays,
                levels: levelsGuess,
                maxLevels: Math.max(RACK_DRAFT_DEFAULTS.maxLevels, levelsGuess),
            },
            placeOnly: Boolean(placeOnly),
            existingBays,
            fromPlace,
        });
    };

    // Clique no mapa durante o gesto: 1º define o início, 2º define o fim
    // (abre o painel); com o painel aberto, um clique novo REFAZ o traçado
    // preservando os parâmetros já digitados.
    const handleTraceClick = (cell) => {
        setDraftApiError(null);
        setRackDraft((previous) => {
            if (!previous) {
                return previous;
            }
            if (!previous.start || previous.end) {
                return { ...previous, start: cell, end: null };
            }
            return { ...previous, end: cell };
        });
    };

    const cancelRackDraft = () => {
        const fromPlace = rackDraft?.fromPlace;
        setRackDraft(null);
        setDraftApiError(null);
        if (fromPlace) {
            onPlaceResolved?.(false);
        }
    };

    /*****************************
     * placeRequest (posicionar fixa / abrir fluxo de rack)
     *****************************/

    const resolvePlace = (ok) => {
        setPlacing(null);
        onPlaceResolved?.(ok);
    };

    const placeType = placeRequest?.type || null;
    const placeCode = placeRequest?.code || null;
    useEffect(() => {
        if (!placeType) {
            setPlacing(null);
            setRackDraft((previous) => (previous?.fromPlace ? null : previous));
            return;
        }
        if (placeType === 'fixed') {
            setRackDraft(null);
            const code = String(placeCode || '').trim().toUpperCase();
            setPlacing({ code });
            showStatus(`${code} — ${label('wms.map.place_fixed_hint')}`);
            return;
        }
        if (placeType === 'rack') {
            // A página pode pedir um rack por aqui; entra no MESMO fluxo de dois
            // cliques do chip da paleta.
            setPlacing(null);
            armRackTrace(true);
        }
        // Reage SÓ à chegada/troca do pedido — view/model são lidos no disparo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeType, placeCode]);

    const commitFixedPlacement = async (cell) => {
        if (placingBusy || !placing) {
            return;
        }
        const result = CreateFixedInLayout(model, cell, placing.code);
        if (!result.ok) {
            showStatus(result.error, true);
            return;
        }
        setPlacingBusy(true);
        try {
            await api.putWarehouseMapLayout(result.model);
            setDraft(result.model);
            setDirty(false);
            onLayoutChange?.(result.model);
            showStatus(label('wms.map.fixed_placed'));
            resolvePlace(true);
        } catch (error) {
            // Fica no modo posicionar — dá para tentar outra célula ou ESC.
            showStatus(error?.message || label('wms.common.load_error'), true);
        } finally {
            setPlacingBusy(false);
        }
    };

    // Monta o payload wire do POST /locations/generate-rack a partir dos opts do
    // rascunho (snake_case só aqui, na borda). O A1 (térreo) NÃO manda peso: o
    // pallet apoia no chão e o backend recusa peso ali.
    const buildGeneratePayload = (opts, bays) => {
        const prefix = NormalizeRackPrefix(opts.rack);
        if (!prefix) {
            return { error: `${label('wms.map.rack_prefix')}: informe 1 ou 2 letras.` };
        }
        if (!Number.isInteger(opts.levels) || opts.levels < 1 || opts.levels > RACK_REAL_MAX_LEVELS) {
            return { error: `${label('wms.map.levels')}: informe um inteiro de 1 a ${RACK_REAL_MAX_LEVELS}.` };
        }
        if (!Number.isInteger(bays) || bays < 1 || bays > RACK_REAL_MAX_BAYS) {
            return { error: `${label('wms.map.bays_count')}: informe de 1 a ${RACK_REAL_MAX_BAYS} blocos.` };
        }
        // UMA largura de vão para TODOS os blocos do rack (M4-4): a mesma medida
        // que desenha o bloco. Exceção por bloco é edição DEPOIS da geração.
        const bayWidth = Number(opts.blockWidth);
        if (!Number.isFinite(bayWidth) || bayWidth <= 0 || bayWidth > 99.99) {
            return { error: `${label('wms.map.block_width')}: informe metros como número positivo (até 99,99).` };
        }
        const weightsByLevel = {};
        for (let level = 2; level <= opts.levels; level += 1) {
            const weight = Number(opts.weights?.[level - 1]);
            if (!Number.isFinite(weight) || weight <= 0) {
                return { error: `${label('wms.generator.weights_title')}: valor inválido no A${level}.` };
            }
            weightsByLevel[String(level)] = weight;
        }
        return {
            payload: {
                rack: prefix,
                bays,
                levels: opts.levels,
                bay_width: bayWidth,
                ...(Object.keys(weightsByLevel).length ? { weights_by_level: weightsByLevel } : {}),
            },
        };
    };

    // CONFIRMAR do mini-form: generate-rack REAL primeiro; só com o dado criado
    // o rack entra no desenho e o layout é salvo. Erro do generate (409 etc.)
    // fica no form com o layout intacto; generate OK + PUT falho vira o banner
    // "mapa não salvo". `placeOnly` pula o generate: o rack já existe, é só o
    // desenho.
    const confirmRackDraft = async () => {
        if (!rackDraftPreview?.result?.ok || creatingRack || !rackDraft) {
            return;
        }
        const opts = rackDraft.opts;
        const prefix = NormalizeRackPrefix(opts.rack);
        setCreatingRack(true);
        setDraftApiError(null);
        if (!rackDraft.placeOnly) {
            const bays = rackDraftPreview.geometry?.sequence?.length || 0;
            const built = buildGeneratePayload(opts, bays);
            if (built.error) {
                setDraftApiError(built.error);
                setCreatingRack(false);
                return;
            }
            try {
                await api.generateWarehouseRack(built.payload);
            } catch (error) {
                if (error?.code === 'NETWORK_ERROR') {
                    setDraftApiError(label('wms.collector.error_network'));
                } else if (error?.status === 409) {
                    setDraftApiError(
                        error.message && error.message !== 'Unknown error'
                            ? error.message
                            : label('wms.generator.rack_exists'),
                    );
                } else {
                    setDraftApiError(error?.message || label('wms.common.load_error'));
                }
                setCreatingRack(false);
                return;
            }
        }

        // Dado real criado (ou já existente) — o rack entra no desenho e o
        // layout é salvo.
        const next = rackDraftPreview.result.model;
        let savedOk = true;
        try {
            await api.putWarehouseMapLayout(next);
        } catch {
            savedOk = false;
        }
        setCreatingRack(false);
        const fromPlace = rackDraft.fromPlace;
        const placeOnly = rackDraft.placeOnly;
        setRackDraft(null);
        setDraft(next);
        setSelected(rackDraftPreview.result.selection || null);
        if (savedOk) {
            setDirty(false);
            setBanner(false);
            onLayoutChange?.(next);
            showStatus(label(placeOnly ? 'wms.map.rack_placed' : 'wms.map.rack_created'));
        } else if (placeOnly) {
            // Sem dado novo para "perder": o desenho fica como rascunho sujo.
            setDirty(true);
            showStatus(label('wms.common.load_error'), true);
        } else {
            setBanner(true);
        }
        onRackCreated?.(prefix);
        if (fromPlace) {
            resolvePlace(true);
        }
    };

    /*****************************
     * Paleta: ÁREA arrasta (chip → solta no canvas); RACK arma o traçado de
     * dois cliques (o gesto do protótipo do dono).
     *****************************/

    const startPaletteDrag = (item, event) => {
        setSelected(null);
        setRackDraft(null);
        setDraftApiError(null);
        setInteraction(null);
        setPaletteDrag(item);
        setDragPoint({ x: event.clientX, y: event.clientY });
    };

    useEffect(() => {
        if (!paletteDrag) {
            return undefined;
        }
        const cellFromClient = (clientX, clientY) => {
            const canvas = canvasRef.current;
            if (!canvas) {
                return null;
            }
            const rect = canvas.getBoundingClientRect();
            return ScreenToCell(view, clientX - rect.left, clientY - rect.top);
        };
        const clearDrag = () => {
            setPaletteDrag(null);
            setPaletteHover(null);
            setDragPoint(null);
        };
        const onMove = (event) => {
            setDragPoint({ x: event.clientX, y: event.clientY });
            setPaletteHover(cellFromClient(event.clientX, event.clientY));
        };
        const onUp = (event) => {
            const cell = cellFromClient(event.clientX, event.clientY);
            const item = paletteDrag;
            clearDrag();
            if (!cell) {
                return; // soltou fora do mapa = cancelou
            }
            runOp(CreateAreaAtCell(model, cell, item.type), label('wms.map.area_created'));
        };
        const onKey = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                clearDrag();
            }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('keydown', onKey, true);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('keydown', onKey, true);
        };
        // runOp/label/showStatus são closures estáveis o bastante para o gesto;
        // o efeito re-arma quando o modelo/vista mudam.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paletteDrag, view, model]);

    /*****************************
     * Gestos no canvas
     *****************************/

    const eventPos = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        return { px: event.clientX - rect.left, py: event.clientY - rect.top };
    };

    const eventCell = (event) => {
        const { px, py } = eventPos(event);
        return ScreenToCell(view, px, py);
    };

    const updateHover = (cell) =>
        setHover((previous) => (previous?.x === cell?.x && previous?.y === cell?.y ? previous : cell));

    const setHoverElementStable = (element) =>
        setHoverElement((previous) => {
            const same =
                (previous?.kind || null) === (element?.kind || null) &&
                (previous?.id || null) === (element?.id || null) &&
                (previous?.x ?? null) === (element?.x ?? null) &&
                (previous?.y ?? null) === (element?.y ?? null);
            return same ? previous : element;
        });

    // Elemento da CÉLULA sob o cursor (hover por elemento — correção do dono):
    // bloco/fixa = a própria célula; corredor = SÓ aquela célula (nunca o rack
    // inteiro); área só interessa no edit (é alvo de arrasto).
    const elementAtCell = (cell) => {
        const obj = GetObjectAt(maps, cell.x, cell.y);
        if (obj) {
            if (obj.kind === 'rack') {
                return { kind: 'rackCell', id: obj.id, x: cell.x, y: cell.y };
            }
            return { kind: obj.kind, id: obj.id };
        }
        if (!editing) {
            return null;
        }
        const areaId = GetTopAreaAt(maps, cell.x, cell.y);
        return areaId ? { kind: 'area', id: areaId } : null;
    };

    // Alvo do clique/arrasto do EDITOR: corredor seleciona o RACK (move junto);
    // bloco e fixa são selecionáveis individualmente.
    const entityAtCell = (cell) => {
        const obj = GetObjectAt(maps, cell.x, cell.y);
        if (obj) {
            return { kind: obj.kind, id: obj.id };
        }
        const areaId = GetTopAreaAt(maps, cell.x, cell.y);
        return areaId ? { kind: 'area', id: areaId } : null;
    };

    const handlePointerMove = (event) => {
        const { px, py } = eventPos(event);
        const cell = ScreenToCell(view, px, py);
        updateHover(cell);

        if (placing) {
            setHoverElementStable(null);
            setCursor('crosshair');
            return;
        }
        if (rackDraft) {
            setHoverElementStable(null);
            setCursor('crosshair');
            return;
        }

        // Modo rota: o único alvo é a PARADA (montagem da rota manual). O
        // destaque por elemento fica desligado — no desenho da rota o que
        // interessa é o percurso, não qual bloco está sob o cursor.
        if (route) {
            setHoverElementStable(null);
            setCursor(onRouteStopClick && routeStopAt(px, py) ? 'pointer' : 'default');
            return;
        }

        if (!editing) {
            const element = cell ? elementAtCell(cell) : null;
            setHoverElementStable(element);
            setCursor(element && onSelect ? 'pointer' : 'default');
            return;
        }

        if (paletteDrag) {
            return; // o gesto da paleta é conduzido pelos listeners de window
        }

        if (interaction) {
            if (cell) {
                setInteraction((previous) => (previous ? { ...previous, current: cell } : previous));
            }
            return;
        }

        const handle = GetSelectionHandleAt(displayModel, view, selected, px, py);
        if (handle) {
            if (handle.mode === 'resize-rack') {
                const rack = GetRackById(displayModel, handle.rackId);
                setCursor(rack?.orientation === 'horizontal' ? 'ew-resize' : 'ns-resize');
            } else {
                setCursor(HANDLE_CURSORS[handle.handle] || 'pointer');
            }
            setHoverElementStable(null);
            return;
        }

        const element = cell ? elementAtCell(cell) : null;
        setHoverElementStable(element);
        setCursor(element ? 'move' : 'default');
    };

    const handlePointerLeave = () => {
        updateHover(null);
        setHoverElementStable(null);
        setCursor('default');
    };

    const handlePointerDown = (event) => {
        if (!editing || paletteDrag || placing || event.button > 0) {
            return;
        }
        const { px, py } = eventPos(event);
        const cell = ScreenToCell(view, px, py);

        // Traçado de rack em curso: o gesto é de CLIQUE (handleClick) — o
        // pointerdown não seleciona nem arrasta nada.
        if (rackDraft) {
            return;
        }

        // 1) Alça da seleção (resize) tem prioridade sobre o corpo.
        const handle = GetSelectionHandleAt(displayModel, view, selected, px, py);
        if (handle) {
            if (handle.mode === 'resize-area') {
                const area = GetAreaById(displayModel, handle.areaId);
                if (!area) {
                    return;
                }
                setInteraction({
                    ...handle,
                    original: { x1: area.x1, y1: area.y1, x2: area.x2, y2: area.y2 },
                    start: cell || { x: area.x2, y: area.y1 },
                    current: cell,
                });
            } else {
                setInteraction({ ...handle, start: cell, current: cell });
            }
            event.currentTarget.setPointerCapture(event.pointerId);
            return;
        }

        if (!cell) {
            setSelected(null);
            return;
        }

        // 2) Corpo: seleciona já no pointerdown e arma o arrasto de mover —
        // soltar sem deslocar é só o clique de seleção.
        const entity = entityAtCell(cell);
        setSelected(entity);
        if (!entity) {
            return;
        }
        if (entity.kind === 'area') {
            setInteraction({ mode: 'move-area', areaId: entity.id, start: cell, current: cell });
        } else if (entity.kind === 'fixed') {
            setInteraction({ mode: 'move-fixed', fixedId: entity.id, start: cell, current: cell });
        } else {
            const rackId = entity.kind === 'rack' ? entity.id : GetBlockById(displayModel, entity.id)?.rackId || null;
            if (!rackId) {
                return;
            }
            setInteraction({ mode: 'move-rack', rackId, start: cell, current: cell });
        }
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const applyInteractionOp = (it) => {
        if (!it.current || !it.start) {
            return;
        }
        const dx = it.current.x - it.start.x;
        const dy = it.current.y - it.start.y;
        if (!dx && !dy) {
            return; // clique (seleção já feita no pointerdown) / alça sem deslocamento
        }
        if (it.mode === 'move-area') {
            runOp(MoveAreaInLayout(model, it.areaId, dx, dy), 'Área movida.');
            return;
        }
        if (it.mode === 'move-rack') {
            runOp(MoveRackInLayout(model, it.rackId, dx, dy), 'Rack movido.');
            return;
        }
        if (it.mode === 'move-fixed') {
            runOp(MoveFixedInLayout(model, it.fixedId, dx, dy), 'Endereço fixo movido.');
            return;
        }
        if (it.mode === 'resize-area') {
            runOp(ResizeAreaInLayout(model, it.areaId, it.original, it.handle, it.current), 'Área redimensionada.');
            return;
        }
        if (it.mode === 'resize-rack') {
            runOp(ResizeRackInLayout(model, it.rackId, it.current), 'Rack redimensionado.');
        }
    };

    const handlePointerUp = (event) => {
        if (!editing || !interaction) {
            return;
        }
        const it = interaction;
        setInteraction(null);
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
            // captura pode já ter sido liberada pelo navegador
        }
        applyInteractionOp(it);
    };

    // Clique = seleção PERSISTENTE do status (onSelect) e o commit do modo
    // posicionar; no edit a seleção acontece no pointerdown.
    const emitSelect = (cell) => {
        if (!onSelect) {
            return;
        }
        const obj = GetObjectAt(maps, cell.x, cell.y);
        if (!obj) {
            onSelect({ kind: 'cell' });
            return;
        }
        if (obj.kind === 'fixed') {
            const item = GetFixedById(displayModel, obj.id);
            if (item) {
                onSelect({ kind: 'fixed', code: item.code });
            }
            return;
        }
        if (obj.kind === 'block') {
            const block = GetBlockById(displayModel, obj.id);
            const rack = block ? GetRackById(displayModel, block.rackId) : null;
            if (block && rack && NormalizeRackPrefix(rack.rack)) {
                onSelect({ kind: 'block', rack: NormalizeRackPrefix(rack.rack), bay: block.bay });
            }
            return;
        }
        const rack = GetRackById(displayModel, obj.id);
        if (rack && NormalizeRackPrefix(rack.rack)) {
            onSelect({ kind: 'rack', rack: NormalizeRackPrefix(rack.rack) });
        }
    };

    const handleClick = (event) => {
        // Montagem da rota MANUAL: o Supervisor toca as paradas na ordem em
        // que quer executá-las, no MESMO mapa em que vê a rota calculada.
        if (route && onRouteStopClick) {
            const { px, py } = eventPos(event);
            const stop = routeStopAt(px, py);
            if (stop) {
                onRouteStopClick(stop.item_key);
                return;
            }
        }
        const cell = eventCell(event);
        if (placing) {
            if (cell) {
                commitFixedPlacement(cell);
            }
            return;
        }
        // Traçado de rack (vale nos dois modos — no status ele chega via
        // placeRequest {type:'rack'}): 1º clique = início, 2º = fim.
        if (rackDraft) {
            if (cell) {
                handleTraceClick(cell);
            }
            return;
        }
        // Editor: clicar num GAP do corredor (célula vazia onde caberia um bloco)
        // abre a confirmação "Adicionar bloco N" (senha depois). Vem ANTES do
        // early-return do edit; seleção/arrasto continuam no pointerdown.
        if (editing && cell) {
            const addable = FindAddableBlockAt(model, cell);
            if (addable) {
                setBlockAddTarget(addable);
                return;
            }
        }
        if (editing || !cell) {
            return;
        }
        emitSelect(cell);
    };

    const handleDoubleClick = (event) => {
        if (!editing || rackDraft || placing) {
            return;
        }
        const cell = eventCell(event);
        if (!cell) {
            return;
        }
        const obj = GetObjectAt(maps, cell.x, cell.y);
        if (obj) {
            if (obj.kind === 'block') {
                setBlockModalId(obj.id);
            } else if (obj.kind === 'rack') {
                setRackModalId(obj.id);
            }
            return;
        }
        const areaId = GetTopAreaAt(maps, cell.x, cell.y);
        if (areaId) {
            setAreaModalId(areaId);
        }
    };

    /*****************************
     * Teclado: Delete apaga a seleção (rack → fluxo com senha; bloco/fixa são
     * explicados, não apagados); ESC desfaz posicionar/rascunho/arrasto/seleção
     *****************************/

    const deleteSelection = () => {
        if (!selected) {
            return;
        }
        if (selected.kind === 'rack') {
            // Rack TEM exclusão no editor (senha + soft-delete no backend):
            // Delete abre o mesmo fluxo do botão "Excluir rack" da ficha.
            requestDeleteRack(selected.id);
            return;
        }
        if (selected.kind === 'block') {
            showStatus(BLOCK_DELETE_BLOCKED_MSG, true);
            return;
        }
        if (selected.kind === 'fixed') {
            showStatus(FIXED_DELETE_BLOCKED_MSG, true);
            return;
        }
        if (selected.kind === 'area') {
            if (runOp(DeleteAreaFromLayout(model, selected.id), 'Área removida.')) {
                setSelected(null);
            }
        }
    };

    useEffect(() => {
        if (!editing && !placing && !rackDraft) {
            return undefined;
        }
        const anyModalOpen = Boolean(areaModalId || rackModalId || blockModalId || blockAddTarget);
        const onKey = (event) => {
            if (event.key !== 'Delete' && event.key !== 'Backspace' && event.key !== 'Escape') {
                return;
            }
            const target = event.target;
            const tag = target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
                return; // digitação em formulário nunca vira gesto do mapa
            }
            if (anyModalOpen) {
                return; // modais de propriedade cuidam do próprio ESC
            }
            if (event.key === 'Escape') {
                let consumed = false;
                if (placing) {
                    resolvePlace(false);
                    consumed = true;
                } else if (rackDraft) {
                    cancelRackDraft();
                    consumed = true;
                }
                if (interaction) {
                    setInteraction(null);
                    consumed = true;
                }
                if (!consumed && selected) {
                    setSelected(null);
                    consumed = true;
                }
                if (consumed) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                return;
            }
            if (!editing || !selected || interaction) {
                return;
            }
            event.preventDefault();
            deleteSelection();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
        // deleteSelection/model mudam juntos via render; recriar o listener a
        // cada mudança relevante é barato e mantém o closure fresco.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing, placing, rackDraft, selected, interaction, model, areaModalId, rackModalId, blockModalId, blockAddTarget]);

    /*****************************
     * Grid
     *****************************/

    const commitGrid = () => {
        const result = SetGridInLayout(model, gridDraft);
        if (!result.ok) {
            showStatus(result.error, true);
            setGridDraft({ w: String(model.grid.w), h: String(model.grid.h), cell: String(model.grid.cell) });
            return;
        }
        const same =
            result.model.grid.w === model.grid.w &&
            result.model.grid.h === model.grid.h &&
            result.model.grid.cell === model.grid.cell;
        if (same) {
            setGridDraft({ w: String(model.grid.w), h: String(model.grid.h), cell: String(model.grid.cell) });
            return;
        }
        runOp(result, 'Grid atualizado.');
    };

    const blurOnEnter = (event) => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    };

    /*****************************
     * Dados dos modais / rodapé
     *****************************/

    const modalArea = editing && areaModalId ? GetAreaById(model, areaModalId) : null;
    const modalRack = editing && rackModalId ? GetRackById(model, rackModalId) : null;
    const modalBlock = editing && blockModalId ? GetBlockById(model, blockModalId) : null;
    const modalBlockRack = modalBlock ? GetRackById(model, modalBlock.rackId) : null;
    // Nome do rack para a confirmação de "Adicionar bloco N" (gap clicado).
    const blockAddRack = blockAddTarget ? GetRackById(model, blockAddTarget.rackId) : null;
    const blockAddRackName = blockAddTarget
        ? blockAddRack?.name || `Rack ${blockAddRack?.rack ?? blockAddTarget.rack}`
        : '';
    const modalRackContainer = modalRack?.areaId ? GetAreaById(model, modalRack.areaId) : null;
    const modalRackBlocks = modalRack ? GetRackBlocks(model, modalRack.id) : [];

    /**
     * Largura do bloco: DESENHO **e** CADASTRO (armadilha corrigida em 11/08).
     *
     * ── O defeito ───────────────────────────────────────────────────────────
     * Existiam dois campos com o nome "Largura do bloco (m)": este, que mexia
     * só no JSON do desenho, e o da Edição em massa, que grava
     * `wms_blocks.bay_width` — o número que o sistema usa de verdade. Como o
     * dono definiu (M4-4) que largura diferente se ajusta **depois do rack
     * gerado**, o lugar onde ele vai clicar é o BLOCO NO MAPA. Dois campos com
     * o mesmo nome fazendo coisas diferentes é armadilha: ele mudaria a
     * largura, salvaria o mapa, e o cadastro continuaria com o valor da
     * geração.
     *
     * ── A ordem: dado REAL primeiro ─────────────────────────────────────────
     * É a mesma disciplina da criação de rack (`generate-rack` antes do PUT do
     * layout): se o cadastro recusar, o desenho NÃO muda — assim o mapa nunca
     * mostra uma largura que o banco não tem. O caminho inverso (desenho
     * primeiro) deixaria a mentira na tela justamente quando a gravação falha.
     *
     * ⚠️ O PATCH é imediato e o desenho continua rascunho até o "Salvar" (com
     * re-senha). Descartar o mapa depois disso NÃO desfaz a largura do
     * cadastro — a mensagem de status diz exatamente isso.
     */
    const saveBlockEdit = async (payload) => {
        const rackPrefix = NormalizeRackPrefix(modalBlockRack?.rack);
        const bay = Number(modalBlock?.bay);
        const widthChanged = Number(payload.width) !== Number(modalBlock?.width);

        if (widthChanged) {
            if (!rackPrefix || !Number.isInteger(bay)) {
                showStatus(BLOCK_WIDTH_NO_REAL_BLOCK_MSG, true);
                return;
            }
            setSavingBlock(true);
            try {
                // O endpoint de edição em massa aceita rack + lista de blocos:
                // um bloco só é uma lista de um.
                await api.bulkUpdateWarehouseLocations({
                    rack: rackPrefix,
                    bays: [bay],
                    set: { bay_width: Number(payload.width) },
                });
            } catch (error) {
                showStatus(error?.message || BLOCK_WIDTH_SAVE_FAILED_MSG, true);
                return;
            } finally {
                setSavingBlock(false);
            }
        }

        if (runOp(UpdateBlockInLayout(model, blockModalId, payload), BLOCK_UPDATED_MSG)) {
            if (widthChanged) {
                showStatus(label('wms.map.block_width_saved'));
            }
            setBlockModalId(null);
        }
    };

    const statusHoverText = useMemo(() => {
        // No modo rota a linha de status é do CHAMADOR (ele explica a rota, o
        // lado a encarar e a montagem manual) — o texto de seleção de bloco
        // ofereceria um clique que este modo não faz.
        if (editing || route || !hover) {
            return null;
        }
        const obj = GetObjectAt(maps, hover.x, hover.y);
        if (!obj) {
            return null;
        }
        if (obj.kind === 'fixed') {
            const item = GetFixedById(displayModel, obj.id);
            return item ? `${item.code} — ${label('wms.map.fixed_hover')}` : null;
        }
        if (obj.kind === 'block') {
            const block = GetBlockById(displayModel, obj.id);
            const rack = block ? GetRackById(displayModel, block.rackId) : null;
            if (!block || !rack) {
                return null;
            }
            const blockKey = BlockKey(rack.rack, block.bay);
            const prefix = `${label('wms.map.rack')} ${rack.rack} · ${label('wms.field.bay')} ${block.bay}`;
            // M9 — no heatmap a linha de status fala em OCUPAÇÃO, que é o que o
            // modo mostra; repetir a contagem de posições do modo `status`
            // deixaria o termômetro sem explicação numérica.
            const heat = heatIndex?.get(blockKey);
            if (heat) {
                const pct = heat.occupancy_pct === null ? '—' : `${heat.occupancy_pct}%`;
                const blocked = heat.blocked_positions > 0 ? ` · ${FormatInt(heat.blocked_positions)} bloqueadas` : '';
                return `${prefix} — ${pct} (${FormatInt(heat.occupied_positions)}/${FormatInt(heat.capacity_positions)})${blocked}`;
            }
            const entry = statusIndex.blocks.get(blockKey);
            if (!entry) {
                return `${prefix} — ${label('wms.map.no_block_data')}`;
            }
            const blockedText = entry.blocked > 0 ? ` (${FormatInt(entry.blocked)} bloqueadas)` : '';
            return `${prefix} — ${FormatInt(entry.total)} posições${blockedText}`;
        }
        const rack = GetRackById(displayModel, obj.id);
        if (!rack) {
            return null;
        }
        return `${label('wms.map.rack')} ${rack.rack} — ${label('wms.map.click_select_rack')}`;
    }, [editing, route, hover, maps, displayModel, statusIndex, heatIndex, label]);

    /*****************************
     * Render
     *****************************/

    const wrapClass = fitPx ? FIT_WRAP_CLASS : filterArea ? AREA_WRAP_CLASS : NATURAL_WRAP_CLASS;
    // O painel de rack só existe DEPOIS dos dois cliques (fases anteriores são
    // só gesto no canvas). A paleta virou faixa em CIMA do mapa (r5, item 5:
    // nada de coluna roubando largura do desenho).
    const draftPanelVisible = Boolean(rackDraft?.start && rackDraft?.end);

    // Traçado em números para o painel ("Vertical · 6 células"). O nº de células
    // é o comprimento do CORREDOR (= nº de blocos quando a pessoa digita
    // "quantos blocos"); em branco é igual ao traçado.
    const traceCells = rackDraftPreview?.geometry?.sequence?.length ?? rackDraftPreview?.axis?.length;
    const traceLabel = rackDraftPreview?.axis
        ? `${label(`wms.map.orientation_${rackDraftPreview.axis.orientation}`)} · ${traceCells} ${label('wms.map.trace_cells')}`
        : null;
    // Placeholder de "quantos blocos": o comprimento do traçado.
    const traceBays = rackDraftPreview?.axis?.length ?? null;

    // Instrução PERSISTENTE do gesto (o statusMsg é transitório).
    const traceHint = rackDraft
        ? !rackDraft.start
            ? label('wms.map.trace_click_start')
            : !rackDraft.end
              ? label('wms.map.trace_click_end')
              : label('wms.map.trace_redo_hint')
        : null;

    const canvasElement = (
        <div
            ref={wrapRef}
            className={`${draftPanelVisible ? 'min-w-0 flex-1 ' : ''}${wrapClass}`}
            style={fitPx ? { height: fitPx } : undefined}
        >
            <canvas
                ref={canvasRef}
                className="block touch-none"
                style={{ cursor }}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                onPointerMove={handlePointerMove}
                onPointerLeave={handlePointerLeave}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
            />
        </div>
    );

    const bannerElement = banner ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-text dark:bg-warning-900/30 dark:text-warning-300">
            <span className="font-medium">{label('wms.map.layout_not_saved')}</span>
            <Button type="button" size="sm" variant="secondary" onClick={retryLayoutSave} loading={bannerBusy}>
                {label('wms.map.save_again')}
            </Button>
        </div>
    ) : null;

    // Painel ÚNICO de rack: coluna lateral de 14rem.
    const sidePanel = draftPanelVisible ? (
        <div className="w-full shrink-0 md:w-56">
            <RackDraftPanel
                key={rackDraft.key}
                initialOpts={rackDraft.opts}
                orientation={draftOrientation}
                traceLabel={traceLabel}
                traceBays={traceBays}
                placeOnly={Boolean(rackDraft.placeOnly)}
                error={draftApiError || (rackDraftPreview?.result?.ok ? null : rackDraftPreview?.result?.error || null)}
                submitting={creatingRack}
                onChange={(opts) => {
                    setDraftApiError(null);
                    // Posicionar rack existente: prefixo e nº de blocos são do
                    // cadastro — o painel não os edita, mas a normalização
                    // poderia zerá-los; preserva.
                    setRackDraft((previous) =>
                        previous
                            ? {
                                  ...previous,
                                  opts: previous.placeOnly
                                      ? { ...opts, rack: previous.opts.rack, bays: previous.opts.bays }
                                      : opts,
                              }
                            : previous,
                    );
                }}
                onConfirm={confirmRackDraft}
                onCancel={cancelRackDraft}
            />
        </div>
    ) : null;

    const statusLine = (
        <div className="flex items-center justify-between gap-3">
            <p
                role="status"
                aria-live="polite"
                className={`min-h-5 text-sm ${
                    statusMsg
                        ? statusMsg.isError
                            ? 'font-semibold text-danger-600 dark:text-danger-400'
                            : 'font-semibold text-success-600 dark:text-success-400'
                        : 'text-mono-500 dark:text-mono-400'
                }`}
            >
                {statusMsg?.text || traceHint || (editing ? label('wms.map.gesture_hint') : statusHoverText || ' ')}
            </p>
            {editing ? (
                <span className="shrink-0 font-mono text-xs text-mono-500 dark:text-mono-400">
                    {hover ? `X=${hover.x} Y=${hover.y}` : ''}
                </span>
            ) : null}
        </div>
    );

    if (!editing) {
        return (
            <div className="space-y-2">
                {bannerElement}
                <div className="flex flex-col gap-3 md:flex-row">
                    {sidePanel}
                    {canvasElement}
                </div>
                {statusLine}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {bannerElement}

            {/* Barra do editor inline: Salvar (re-senha) / Descartar + grid */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        type="button"
                        onClick={requestSave}
                        loading={saving}
                        disabled={creatingRack || bannerBusy}
                    >
                        {label('wms.common.save')}
                    </Button>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={discardChanges}
                        disabled={saving || creatingRack || (!dirty && !banner)}
                    >
                        {label('wms.map.discard_confirm')}
                    </Button>
                    {dirty ? (
                        <span className="text-xs font-medium text-warning-text dark:text-warning-300">
                            {label('wms.map.unsaved')}
                        </span>
                    ) : null}
                </div>
                <div className="flex items-end gap-2">
                    <div className="w-20">
                        <Input
                            label={label('wms.map.grid_w')}
                            type="number"
                            min={GRID_LIMITS.minSize}
                            max={GRID_LIMITS.maxSize}
                            value={gridDraft.w}
                            onChange={(event) => setGridDraft((prev) => ({ ...prev, w: event.target.value }))}
                            onBlur={commitGrid}
                            onKeyDown={blurOnEnter}
                        />
                    </div>
                    <div className="w-20">
                        <Input
                            label={label('wms.map.grid_h')}
                            type="number"
                            min={GRID_LIMITS.minSize}
                            max={GRID_LIMITS.maxSize}
                            value={gridDraft.h}
                            onChange={(event) => setGridDraft((prev) => ({ ...prev, h: event.target.value }))}
                            onBlur={commitGrid}
                            onKeyDown={blurOnEnter}
                        />
                    </div>
                    <div className="w-24">
                        <Input
                            label={label('wms.map.grid_cell')}
                            type="number"
                            min={GRID_LIMITS.minCell}
                            max={GRID_LIMITS.maxCell}
                            value={gridDraft.cell}
                            onChange={(event) => setGridDraft((prev) => ({ ...prev, cell: event.target.value }))}
                            onBlur={commitGrid}
                            onKeyDown={blurOnEnter}
                        />
                    </div>
                </div>
            </div>

            {/* Paleta em FAIXA (não rouba largura do desenho) + canvas; o
                painel de rack entra na lateral só depois dos dois cliques. */}
            <MapPalette
                onDragStart={startPaletteDrag}
                onPickRack={() => armRackTrace(false)}
                onPlaceRack={(prefix) => armRackTrace(false, prefix)}
                rackArmed={Boolean(rackDraft)}
                armedRack={rackDraft?.placeOnly ? rackDraft.opts.rack : null}
                unplacedRacks={unplacedRacks}
                disabled={saving || creatingRack}
            />
            <div className="flex flex-col gap-3 md:flex-row">
                {sidePanel}
                {canvasElement}
            </div>

            {statusLine}

            {/* Badge que segue o cursor durante o arrasto da paleta */}
            {paletteDrag && dragPoint ? (
                <div
                    aria-hidden="true"
                    className="pointer-events-none fixed z-[60] -translate-x-1/2 -translate-y-[140%] rounded-full border border-mono-300 bg-white px-3 py-1 text-xs font-semibold text-mono-700 shadow-lg-mono dark:border-mono-700 dark:bg-mono-900 dark:text-mono-200"
                    style={{ left: dragPoint.x, top: dragPoint.y }}
                >
                    {label(PaletteItemLabelKey(paletteDrag))}
                </div>
            ) : null}

            <AreaEditModal
                isOpen={Boolean(modalArea)}
                area={modalArea}
                onChangeType={(type) =>
                    runOp(UpdateAreaTypeInLayout(model, areaModalId, type), 'Tipo da área atualizado.')
                }
                onDelete={() => {
                    // Fecha o modal SEMPRE — se a exclusão falhar (área com
                    // racks reais), a explicação fica na linha de status.
                    if (runOp(DeleteAreaFromLayout(model, areaModalId), 'Área removida.')) {
                        setSelected(null);
                    }
                    setAreaModalId(null);
                }}
                onClose={() => setAreaModalId(null)}
            />
            <RackEditModal
                isOpen={Boolean(modalRack)}
                rack={modalRack}
                model={model}
                blocksCount={modalRackBlocks.length}
                busy={rackOpBusy}
                containerLabel={
                    modalRackContainer ? modalRackContainer.label || areaTypeLabel(modalRackContainer.type) : null
                }
                onSave={(payload) => {
                    if (runOp(UpdateRackInLayout(model, rackModalId, payload), 'Rack atualizado.')) {
                        setRackModalId(null);
                    }
                }}
                onDeleteRack={() => requestDeleteRack(rackModalId)}
                onClose={() => setRackModalId(null)}
            />
            <BlockEditModal
                isOpen={Boolean(modalBlock)}
                block={modalBlock}
                rack={modalBlockRack}
                saving={savingBlock}
                busy={rackOpBusy}
                onSave={saveBlockEdit}
                onDeleteBlock={() => requestDeleteBlock(blockModalId)}
                onClose={() => setBlockModalId(null)}
            />
            <BlockAddModal
                isOpen={Boolean(blockAddTarget)}
                target={blockAddTarget}
                rackName={blockAddRackName}
                busy={rackOpBusy}
                onConfirm={() => requestAddBlock(blockAddTarget.rackId, blockAddTarget.bay)}
                onClose={() => setBlockAddTarget(null)}
            />

            <ConfirmPasswordModal
                isOpen={passwordOpen}
                onClose={() => setPasswordOpen(false)}
                onConfirmed={handlePasswordConfirmed}
            />
        </div>
    );
};

export default WarehouseMap;
